import { get, camelCase } from 'lodash'
import * as deepmerge from 'deepmerge'
import { systemComponents } from '../../lib/helpers'
import { FormDesignerOptions } from '../../layout-designer/FormDesignerOptions'
import fontAwesomeIconList from '../../../../utils/fontAwesomeIconList'
import { SOURCE_TYPES } from '../../../list-new/lib/list-constants'

const OPERATIONS = [
  { key: '=', value: '=' },
  { key: '<>', value: '<>' },
  { key: '!=', value: '!=' }, // @todo kideríteni hol melyik használható és egységesíteni (!=, <>)
  { key: '>', value: '>' },
  { key: '>=', value: '>=' },
  { key: '<', value: '<' },
  { key: '<=', value: '<=' },
  { key: 'like', value: 'like' },
  { key: 'in', value: 'in' },
  { key: 'is', value: 'is' },
]

const ROW_STYLE_APPLIED_TO = ['ROW', 'COLUMN', 'CELL']
const ROW_STYLE_DEFAULT_APPLIED_TO = 'ROW'

export const PANEL_TYPES = {
  DYN_FORM: 'DynForm',
  FORM_VIEW: 'FormView',
  LIST: 'List',
  EDITABLE_LIST: 'EditableList',
  QUERY_LIST: 'QueryList',
  BUTTON_LIST: 'ButtonList',
  TEMPLATE_VIEWER: 'TemplateViewer',
  RELATIONSHIPS: 'Relationships',
  LIST_VIEW: 'ListView',
  QUERY_LIST_VIEW: 'QueryListView',
  PROCESS_LIST_VIEW: 'ProcessListView',
  TEMPLATE_VIEW: 'TemplateView',
  LIST_FILTER: 'ListFilter',
  QUERY_LIST_FILTER: 'QueryListFilter',
  PROCESS_LIST_FILTER: 'ProcessListFilter',
  FULL_CALENDAR_WRAPPER: 'FullCalendarWrapper',
  DOCUMENT_LIST: 'DocumentList',
  CUSTOM_COMPONENT: 'CustomComponent',
  CHART_JS: 'ChartJS',
  CLOCK: 'Clock',
  COMPETENCE: 'Competence',
  KANBAN_WRAPPER: 'KanbanWrapper',
  TASK_BOARD: 'TaskBoard',
  STATUS_LINE: 'StatusLine',
  LEAFLET_WRAPPER: 'LeafletWrapper',
  PROCESS_BUILDER: 'ProcessBuilder',
  QUERY_BUILDER: 'QueryBuilder',
  MENU_EDITOR: 'MenuEditor',
  WORKFLOW_EDITOR: 'WorkflowDesigner',
  WORKFLOW_LIST: 'WorkflowList',
  WORKFLOW_VIEWER: 'WorkflowViewer',
  MONGO_LIST: 'MongoList',
  TASK_LIST: 'TaskList',
  JSON_FORM: 'JsonForm',
  DASHBOARD: 'Dashboard',
  API: 'API',
  CAROUSEL: 'Carousel',
  FORM_DESIGNER: 'FormDesigner',
  FORM_DESIGNER_OLD: 'FormDesignerOld',
  FORM_DATA_VERSION: 'FormDataVersion',
  GANTT: 'Gantt',
  HELPY: 'Helpy',
  HELPY_DESIGNER: 'HelpyDesigner',
  HELPY_FORM: 'HelpyForm',
  ENVIRONMENT_MANAGER: 'EnvironmentManager',
  PROCESS_LIST: 'ProcessList',
  PROCESS_QUEUE_LIST: 'ProcessQueueList',
  MENU_PANEL: 'MenuPanel',
  RAW_SQL_LIST: 'RawSqlList',
  RAW_SQL_EDITOR: 'RawSqlEditor',
  TEMPLATED_LIST: 'TemplatedList',
  DYN_FORM_NEW: 'DynFormNew',
}

export const defaultOptions = function (subsystems, self) {
  return {
    schema: {
      title: 'Root',
      type: 'object',
      format: 'categories',
      options: {
        collapsed: false,
      },
      properties: {
        pure: {
          title: 'pure',
          type: 'boolean',
        },
        keepAlive: {
          title: 'keep_alive',
          type: 'boolean',
        },
        system: {
          title: 'visible_on_this_subsystem',
          type: 'integer',
          enum: ['', ...subsystems.map((value) => value.id)],
          options: {
            enum_titles: ['', ...subsystems.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
        },
        base: {
          title: 'base',
          type: 'object',
          options: {
            collapsed: false,
          },
          properties: {
            hidden: {
              title: 'hidden_expression',
              type: 'string',
            },
          },
        },
        dictionary: {
          title: 'dictionary',
          type: 'object',
          options: {
            collapsed: false,
          },
          format: 'array',
          uniqueItems: true,
          items: {
            type: 'object',
            properties: {
              name: {
                title: 'name',
                type: 'string',
              },
              value: {
                title: 'value',
                type: 'string',
              },
            },
          },
        },
        collapsed: {
          title: 'collapsed',
          type: 'object',
          options: {
            collapsed: false,
          },
          properties: {
            type: {
              title: 'type',
              type: 'string',
            },
          },
        },
        widget: {
          title: 'widget',
          type: 'object',
          options: {
            collapsed: false,
          },
          properties: {
            position: {
              title: 'position',
              type: 'object',
              format: 'grid',
              properties: {
                x: {
                  title: 'X',
                  type: 'integer',
                },
                y: {
                  title: 'Y',
                  type: 'integer',
                },
                width: {
                  title: 'width',
                  type: 'integer',
                  default: 3,
                },
                height: {
                  title: 'height',
                  type: 'integer',
                  default: 2,
                },

                'min-width': {
                  title: 'min_width',
                  type: 'integer',
                  default: 2,
                },
                'min-height': {
                  title: 'min_height',
                  type: 'integer',
                  default: 2,
                },
                'max-width': {
                  title: 'max_width',
                  type: 'integer',
                  default: 12,
                },
                'max-height': {
                  title: 'max_height',
                  type: 'integer',
                  default: 12,
                },
                'change-width': {
                  title: 'change_width',
                  type: 'integer',
                  default: 5,
                },
                'change-height': {
                  title: 'change_height',
                  type: 'integer',
                  default: 5,
                },
              },
            },
            style: {
              title: 'style',
              type: 'string',
              enum: ['Default', 'Blue', 'Green', 'Orange', 'Red', 'Yellow', 'Transparent', 'Dark', 'Purple'],
            },
            ico: {
              title: 'icon_(fontawesome_4)',
              // format: 'select2',
              type: 'string',
              enum: fontAwesomeIconList,
            },
            // material icon extension @Jana
            materialIcon: {
              title: 'icon_(material_icons)',
              type: 'string',
            },
            enable: {
              title: 'enable',
              type: 'boolean',
              format: 'checkbox',
            },
            active: {
              title: 'active',
              type: 'boolean',
              format: 'checkbox',
            },
            removable: {
              title: 'removable',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
          },
        },
        helpy: {
          title: 'helpy',
          type: 'object',
          options: {
            collapsed: false,
          },
          properties: {
            title: {
              title: 'title',
              type: 'string',
            },
            content: {
              title: 'description',
              type: 'string',
              format: 'textarea',
            },
          },
        },
      },
    },
  }
}

export const getSpecifiedOptions = function (
  self,
  type,
  customComponents,
  jsonForms,
  subsystems,
  menus,
  mongoIdSource,
  queryIdSource,
  processIdSource,
  rawSqlIdSource,
  templateIdSource,
  urlSource
) {
  const options = defaultOptions(subsystems, self)
  switch (type) {
    case PANEL_TYPES.DYN_FORM: {
      const specifiedOptions = {
        subformView: {
          title: 'subform_view',
          type: 'string',
          enum: ['Default', 'Grid', 'List', 'FormGrid', 'CollapsibleGrid'],
          default: 'Default',
        },
        showNotiOnExp: {
          title: 'show_notification_when_expression_calculates:',
          type: 'object',
          collapsed: false,
          properties: {
            value: {
              title: 'value',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
            hidden: {
              title: 'hidden_property',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
          },
        },
        showIsDirtyWarningMessage: {
          title: 'show_is_dirty_warning_message',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        usernameHidden: {
          title: 'username_hidden',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        groupAutoCollapse: {
          title: 'group_auto_collapse',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        collapsedDefaultClose: {
          title: 'group_collapsed_default_close',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        formDataVersionButtons: {
          title: 'form_data_version_button_settings',
          type: 'object',
          collapsed: false,
          properties: {
            showCloneButton: {
              title: 'show_clone_button',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            showCopyButton: {
              title: 'show_copy_button',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            showVersionButton: {
              title: 'show_version_button',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
          },
        },
        showHistory: {
          title: 'enable_form_data_history',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        showFormVersionInfo: {
          title: 'enable_form_version_information',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        blockchainEnabled: {
          title: 'enable_blockchain',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        mergeUrlParams: {
          title: 'merge_data_with_url_params_(this_checkbox_is_only_for_the_updatemethod)',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        saveOnEnter: {
          title: 'save_form_on_enter',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        buttons: {
          type: 'object',
          title: 'buttons_override',
          options: {
            collapsed: false,
          },
          properties: {
            save: {
              type: 'object',
              format: 'grid',
              title: 'save_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'save',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_save',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            saveAndNew: {
              type: 'object',
              format: 'grid',
              title: 'save_and_new_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'saveAndNew',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_saveAndNew',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            saveAndClose: {
              type: 'object',
              format: 'grid',
              title: 'save_and_close_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'saveAndClose',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_saveAndClose',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            close: {
              type: 'object',
              format: 'grid',
              title: 'close_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'index',
                  disable: true,
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_close',
                  options: { grid_columns: 4 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 4 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 3 },
                },
              },
            },
          },
        },
        idForm: {
          type: 'string',
          title: 'form',
          enum: ['', ...mongoIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
              dropdownAutoWidth: true,
              // containerCss: {'display':'block', 'width':'100%'},
              width: '300px',
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
      }

      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.FORM_VIEW: {
      const specifiedOptions = {
        subformView: {
          title: 'subform_view',
          type: 'string',
          enum: ['Default', 'Grid', 'List'],
          default: 'Default',
        },
        usernameHidden: {
          title: 'username_hidden',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        groupAutoCollapse: {
          title: 'group_auto_collapse',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        collapsedDefaultClose: {
          title: 'group_collapsed_default_close',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        idForm: {
          type: 'string',
          title: 'form',
          enum: ['', ...mongoIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
              dropdownAutoWidth: true,
              width: '300px',
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
        buttons: {
          type: 'object',
          title: 'buttons_override',
          options: {
            collapsed: false,
          },
          properties: {
            close: {
              type: 'object',
              format: 'grid',
              title: 'close_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'index',
                  disable: true,
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_close',
                  options: { grid_columns: 4 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 4 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 3 },
                },
              },
            },
          },
        },
      }
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          options: {
            collapsed: false,
          },
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            isColumnWidthEditable: {
              title: 'column_width_editable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            exportOriginalType: {
              title: 'export_list_in_original_type',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateDate: {
              title: 'show_createdate_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateUser: {
              title: 'show_createuser_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showRelations: {
              title: 'show_relations',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            summaryRowDefault: {
              title: 'summary_row_default',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchBarIsCollapsed: {
              title: 'searchbar_is_collapsed',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: true,
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  default: 'gridView',
                  options: { grid_columns: 12 },
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            dbClickRedirect: {
              title: 'doubleclick_redirect',
              type: 'object',
              format: 'grid',
              uniqueItems: true,
              properties: {
                active: {
                  title: 'active',
                  type: 'boolean',
                  format: 'checkbox',
                },
                popup: {
                  title: 'popup',
                  type: 'boolean',
                  format: 'checkbox',
                },
                url: {
                  title: 'URL',
                  type: 'string',
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                    default: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            columnRedirect: {
              type: 'array',
              format: 'table',
              title: 'columns_redirect',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'array',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'order',
            properties: {
              column: { title: 'column_name', type: 'string' },
              dir: {
                title: 'direction',
                type: 'string',
                enum: ['asc', 'desc'],
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        hiddenSearchData: {
          type: 'object',
          title: 'extra_filter',
          properties: {
            where: {
              type: 'array',
              format: 'table',
              title: 'conditions',
              items: {
                type: 'object',
                title: 'condition',
                properties: {
                  key: { title: 'column_name', type: 'string' },
                  op: {
                    title: 'operation',
                    type: 'string',
                    enum: OPERATIONS.map((op) => op.value),
                    options: {
                      enum_titles: OPERATIONS.map((op) => op.key),
                      select2_options: {
                        default: '=',
                        allowClear: false,
                      },
                    },
                  },
                  value: { title: 'value', type: 'string' },
                },
              },
            },
            logic: {
              type: 'string',
              title: 'logic',
            },
            filterExp: {
              type: 'object',
              title: 'filter_expression',
              properties: {
                table: { type: 'string', title: 'table_name' },
                //                                  select: {type: 'array', title: 'Field name pl: ['ID_ALLASKERESO']'},
                select: {
                  type: 'array',
                  format: 'table',
                  title: 'field_name_ex:_ID_ALLASKERESO',
                  items: {
                    type: 'string',
                    title: 'field_name',
                    properties: {
                      name: { title: 'Name', type: 'string' },
                    },
                  },
                },
                field: { type: 'string', title: 'field_name' },
                operation: {
                  title: 'operation',
                  type: 'string',
                  enum: ['', 'in', 'not in'],
                },
                where: {
                  type: 'array',
                  format: 'table',
                  title: 'conditions',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    title: 'condition',
                    properties: {
                      key: { title: 'column_name', type: 'string' },
                      op: {
                        title: 'operation',
                        type: 'string',
                        enum: OPERATIONS.map((op) => op.value),
                        options: {
                          enum_titles: OPERATIONS.map((op) => op.key),
                          select2_options: {
                            default: '=',
                            allowClear: false,
                          },
                        },
                      },
                      value: { title: 'value', type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
        mongoId: {
          type: 'string',
          title: 'list',
          enum: ['', ...mongoIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        customLayout: {
          title: 'custom_layout',
          type: 'string',
          format: 'textarea',
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                mongoId: {
                  type: 'string',
                  title: 'Mongo Id',
                  enum: ['', ...mongoIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.TASK_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          options: {
            collapsed: false,
          },
          properties: {
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateDate: {
              title: 'show_createdate_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateUser: {
              title: 'show_createuser_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 6 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 6 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: true,
                  options: { grid_columns: 4 },
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: false,
                  options: { grid_columns: 4 },
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: false,
                  options: { grid_columns: 4 },
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  options: { grid_columns: 12 },
                  default: 'gridView',
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'columns',
                properties: {
                  name: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  visible: {
                    title: 'show_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'object',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          properties: {
            column: { title: 'column_name', type: 'string' },
            dir: {
              title: 'direction',
              type: 'string',
              enum: ['asc', 'desc'],
            },
          },
        },
        hiddenSearchData: {
          type: 'object',
          title: 'extra_filter',
          properties: {
            where: {
              type: 'array',
              format: 'table',
              title: 'conditions',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'condition',
                properties: {
                  key: { title: 'column_name', type: 'string' },
                  op: {
                    title: 'operation',
                    type: 'string',
                    enum: OPERATIONS.map((op) => op.value),
                    options: {
                      enum_titles: OPERATIONS.map((op) => op.key),
                      select2_options: {
                        default: '=',
                        allowClear: false,
                      },
                    },
                  },
                  value: { title: 'value', type: 'string' },
                },
              },
            },
            logic: {
              type: 'string',
              title: 'logic',
            },
            filterExp: {
              type: 'object',
              title: 'filter_expression',
              properties: {
                table: { type: 'string', title: 'table_name' },
                select: {
                  type: 'array',
                  format: 'table',
                  title: 'field_name_ex:_ID_FIELD',
                  items: {
                    type: 'string',
                    title: 'field_name',
                    properties: {
                      name: { title: 'Name', type: 'string' },
                    },
                  },
                },
                field: { type: 'string', title: 'field_name' },
                operation: {
                  title: 'operation',
                  type: 'string',
                  enum: ['', 'in', 'not in'],
                },
                where: {
                  type: 'array',
                  format: 'table',
                  title: 'conditions',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    title: 'condition',
                    properties: {
                      key: { title: 'column_name', type: 'string' },
                      op: {
                        title: 'operation',
                        type: 'string',
                        enum: OPERATIONS.map((op) => op.value),
                        options: {
                          enum_titles: OPERATIONS.map((op) => op.key),
                          select2_options: {
                            default: '=',
                            allowClear: false,
                          },
                        },
                      },
                      value: { title: 'value', type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
      }
      const specifiedOptionsCollapsed = {}

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.QUERY_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            isColumnWidthEditable: {
              title: 'column_width_editable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showRelations: {
              title: 'show_relations',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            summaryRowDefault: {
              title: 'summary_row_default',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 12 },
              default: false,
            },
            searchBarIsCollapsed: {
              title: 'searchbar_is_collapsed',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: true,
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: false,
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  default: 'gridView',
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            dbClickRedirect: {
              title: 'doubleclick_redirect',
              type: 'object',
              format: 'table',
              uniqueItems: true,
              properties: {
                active: {
                  title: 'active',
                  type: 'boolean',
                  format: 'checkbox',
                },
                popup: {
                  title: 'popup',
                  type: 'boolean',
                  format: 'checkbox',
                },
                url: {
                  title: 'URL',
                  type: 'string',
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  showInList: {
                    title: 'show_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInList: {
                    title: 'available_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInDetailView: {
                    title: 'available_in_detail_view',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        queryId: {
          type: 'string',
          title: 'query_list',
          enum: ['', ...queryIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...queryIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                queryId: {
                  type: 'string',
                  title: 'Query Id',
                  enum: ['', ...queryIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...queryIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.MONGO_LIST: {
      const specifiedOptions = {
        mongoList: {
          type: 'object',
          title: 'mongo_list',
          options: {
            collapsed: false,
          },
          properties: {
            extraOptions: {
              type: 'object',
              title: 'extra_options',
              format: 'grid',
              properties: {
                groupFilter: {
                  title: 'group_filter',
                  type: 'object',
                  options: {
                    collapsed: false,
                  },
                  format: 'array',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    properties: {
                      name: {
                        title: 'group_by',
                        type: 'string',
                      },
                      value: {
                        title: 'order_by',
                        type: 'string',
                      },
                    },
                  },
                },
                dbClickRedirect: {
                  title: 'doubleclick_redirect',
                  type: 'object',
                  format: 'grid',
                  uniqueItems: true,
                  properties: {
                    active: {
                      title: 'active',
                      type: 'boolean',
                      format: 'checkbox',
                    },
                    popup: {
                      title: 'popup',
                      type: 'boolean',
                      format: 'checkbox',
                    },
                    url: {
                      title: 'URL',
                      type: 'string',
                    },
                  },
                },
              },
            },
            displayFooterButtons: {
              title: 'display_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            footerbuttons: {
              type: 'array',
              format: 'table',
              title: 'footer_buttons',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'button',
                properties: {
                  type: {
                    title: 'type',
                    type: 'string',
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'table',
              title: 'buttons',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'button',
                properties: {
                  type: {
                    title: 'type',
                    type: 'string',
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                  },
                },
              },
            },
            collection: { title: 'collection', type: 'string' },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column',
                properties: {
                  type: {
                    type: 'string',
                    title: 'type',
                    options: {
                      inputAttributes: {
                        placeholder: 'column type...',
                      },
                    },
                  },
                  name: {
                    type: 'string',
                    title: 'name',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    type: 'string',
                    title: 'label',
                    options: {
                      inputAttributes: {
                        placeholder: 'column label...',
                      },
                    },
                  },
                },
              },
            },
            exportColumns: {
              type: 'array',
              format: 'table',
              title: 'export_columns',
              items: {
                type: 'string',
                title: 'add_column',
                properties: {
                  name: { title: 'Name', type: 'string' },
                },
              },
            },
            exportAll: {
              title: 'export_all',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
            exportPage: {
              title: 'export_page',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
            urlParams: {
              title: 'urlParams',
              type: 'object',
              options: {
                collapsed: true,
              },
              format: 'array',
              uniqueItems: true,
              items: {
                type: 'object',
                properties: {
                  name: {
                    title: 'name',
                    type: 'string',
                  },
                  value: {
                    title: 'value',
                    type: 'string',
                  },
                },
              },
            },
          },
        },
      }

      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.PROCESS_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          options: {
            collapsed: false,
          },
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            isSearchable: {
              title: 'is_list_searchable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateDate: {
              title: 'show_createdate_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateUser: {
              title: 'show_createuser_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showRelations: {
              title: 'show_relations',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            summaryRowDefault: {
              title: 'summary_row_default',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 12 },
              default: false,
            },
            searchBarIsCollapsed: {
              title: 'searchbar_is_collapsed',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: true,
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  default: 'gridView',
                  options: { grid_columns: 12 },
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            dbClickRedirect: {
              title: 'doubleclick_redirect',
              type: 'object',
              format: 'grid',
              uniqueItems: true,
              properties: {
                active: {
                  title: 'active',
                  type: 'boolean',
                  format: 'checkbox',
                },
                popup: {
                  title: 'popup',
                  type: 'boolean',
                  format: 'checkbox',
                },
                url: {
                  title: 'URL',
                  type: 'string',
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  type: {
                    title: 'type',
                    type: 'string',
                    enum: ['text', 'number', 'date', 'datetime-local'],
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  showInList: {
                    title: 'show_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInList: {
                    title: 'available_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInDetailView: {
                    title: 'available_in_detail_view',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                    default: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'array',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'order',
            properties: {
              column: { title: 'column_name', type: 'string' },
              dir: {
                title: 'direction',
                type: 'string',
                enum: ['asc', 'desc'],
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        hiddenSearchData: {
          type: 'object',
          title: 'extra_filter',
          properties: {
            where: {
              type: 'array',
              format: 'table',
              title: 'conditions',
              items: {
                type: 'object',
                title: 'condition',
                properties: {
                  key: { title: 'column_name', type: 'string' },
                  op: {
                    title: 'operation',
                    type: 'string',
                    enum: OPERATIONS.map((op) => op.value),
                    options: {
                      enum_titles: OPERATIONS.map((op) => op.key),
                      select2_options: {
                        default: '=',
                        allowClear: false,
                      },
                    },
                  },
                  value: { title: 'value', type: 'string' },
                },
              },
            },
            logic: {
              type: 'string',
              title: 'logic',
            },
            filterExp: {
              type: 'object',
              title: 'filter_expression',
              properties: {
                table: { type: 'string', title: 'table_name' },
                select: {
                  type: 'array',
                  format: 'table',
                  title: 'field_name_ex:_ID_ALLASKERESO',
                  items: {
                    type: 'string',
                    title: 'field_name',
                    properties: {
                      name: { title: 'Name', type: 'string' },
                    },
                  },
                },
                field: { type: 'string', title: 'field_name' },
                operation: {
                  title: 'operation',
                  type: 'string',
                  enum: ['', 'in', 'not in'],
                },
                where: {
                  type: 'array',
                  format: 'table',
                  title: 'conditions',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    title: 'condition',
                    properties: {
                      key: { title: 'column_name', type: 'string' },
                      op: {
                        title: 'operation',
                        type: 'string',
                        enum: OPERATIONS.map((op) => op.value),
                        options: {
                          enum_titles: OPERATIONS.map((op) => op.key),
                          select2_options: {
                            default: '=',
                            allowClear: false,
                          },
                        },
                      },
                      value: { title: 'value', type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
        processId: {
          type: 'string',
          title: 'process_list',
          enum: ['', ...processIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...processIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
        mappingOptions: {
          title: 'mapping_options',
          type: 'object',
          properties: {
            request: {
              type: 'object',
              title: 'request',
              description: 'path_of_prop_in_request_object_(eg.:_search,_columns)',
              properties: {
                search: { title: 'search', type: 'string' },
                hiddenSearch: { title: 'hidden_search', type: 'string' },
                // ...
              },
            },
            response: {
              type: 'object',
              title: 'response',
              description: 'path_of_prop_in_response_object_(eg.:_data,_primaryKey)',
              properties: {
                data: { title: 'columns_data', type: 'string' },
                recordsTotal: { title: 'records_total', type: 'string' },
                recordsFiltered: { title: 'records_filtered', type: 'string' },
                primaryKey: { title: 'primary_key', type: 'string' },
                mainTable: { title: 'main_table', type: 'string' },
                aggregateRow: { title: 'aggregate_row', type: 'string' },
                modelIdOnly: {
                  title: 'model_id_only',
                  type: 'boolean',
                  format: 'checkbox',
                  default: false,
                },
              },
            },
          },
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        customLayout: {
          title: 'custom_layout',
          type: 'string',
          format: 'textarea',
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                mongoId: {
                  type: 'string',
                  title: 'process_id',
                  enum: ['', ...processIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...processIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.PROCESS_QUEUE_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          options: {
            collapsed: false,
          },
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            isSearchable: {
              title: 'is_list_searchable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showCreateDate: {
              title: 'show_createdate_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateUser: {
              title: 'show_createuser_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            summaryRowDefault: {
              title: 'summary_row_default',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 12 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: true,
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  default: 'gridView',
                  options: { grid_columns: 12 },
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  type: {
                    title: 'type',
                    type: 'string',
                    enum: ['text', 'number', 'date'],
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  showInList: {
                    title: 'show_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInList: {
                    title: 'available_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInDetailView: {
                    title: 'available_in_detail_view',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                    default: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'array',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'order',
            properties: {
              column: { title: 'column_name', type: 'string' },
              dir: {
                title: 'direction',
                type: 'string',
                enum: ['asc', 'desc'],
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        hiddenSearchData: {
          type: 'object',
          title: 'extra_filter',
          properties: {
            where: {
              type: 'array',
              format: 'table',
              title: 'conditions',
              items: {
                type: 'object',
                title: 'condition',
                properties: {
                  key: { title: 'column_name', type: 'string' },
                  op: {
                    title: 'operation',
                    type: 'string',
                    enum: OPERATIONS.map((op) => op.value),
                    options: {
                      enum_titles: OPERATIONS.map((op) => op.key),
                      select2_options: {
                        default: '=',
                        allowClear: false,
                      },
                    },
                  },
                  value: { title: 'value', type: 'string' },
                },
              },
            },
            logic: {
              type: 'string',
              title: 'logic',
            },
            filterExp: {
              type: 'object',
              title: 'filter_expression',
              properties: {
                table: { type: 'string', title: 'table_name' },
                select: {
                  type: 'array',
                  format: 'table',
                  title: 'field_name_ex:_ID_ALLASKERESO',
                  items: {
                    type: 'string',
                    title: 'field_name',
                    properties: {
                      name: { title: 'Name', type: 'string' },
                    },
                  },
                },
                field: { type: 'string', title: 'field_name' },
                operation: {
                  title: 'operation',
                  type: 'string',
                  enum: ['', 'in', 'not in'],
                },
                where: {
                  type: 'array',
                  format: 'table',
                  title: 'conditions',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    title: 'condition',
                    properties: {
                      key: { title: 'column_name', type: 'string' },
                      op: {
                        title: 'operation',
                        type: 'string',
                        enum: OPERATIONS.map((op) => op.value),
                        options: {
                          enum_titles: OPERATIONS.map((op) => op.key),
                          select2_options: {
                            default: '=',
                            allowClear: false,
                          },
                        },
                      },
                      value: { title: 'value', type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        customLayout: {
          title: 'custom_layout',
          type: 'string',
          format: 'textarea',
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                mongoId: {
                  type: 'string',
                  title: 'process_id',
                  enum: ['', ...processIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...processIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.LEAFLET_WRAPPER: {
      const specifiedOptions = {
        entities: {
          type: 'array',
          format: 'grid-strict',
          title: 'entities',
          uniqueItems: true,
          items: {
            type: 'object',
            format: 'grid-strict',
            title: 'entity',
            options: {
              disable_collapse: true,
            },
            properties: {
              label: {
                type: 'string',
                title: 'map_layer_title',
              },
              mongoId: {
                type: 'string',
                title: 'entity',
                enum: ['', ...mongoIdSource.map((value) => value.value)],
                options: {
                  grid_break: true,
                  enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                  select2_options: {
                    placeholder: '',
                    allowClear: true,
                    dropdownAutoWidth: true,
                  },
                },
                minLength: 1,
                propertyOrder: 0,
              },
              featureParams: {
                type: 'array',
                format: 'grid-strict',
                title: 'leaflet_layer_parameters',
                minLength: 1,
                items: {
                  type: 'object',
                  format: 'grid-strict',
                  title: 'data_source_field',
                  options: {
                    disable_collapse: true,
                  },
                  properties: {
                    field: {
                      title: 'field_name',
                      type: 'string',
                      options: {
                        grid_columns: 6,
                      },
                    },
                    source: {
                      title: 'feature_source_type',
                      type: 'string',
                      enum: ['Coordinate', 'GeoJSON'],
                      options: {
                        enum_titles: ['Coordinate', 'GeoJSON'],
                        select2_options: {
                          default: 'GeoJSON',
                          allowClear: false,
                        },
                        grid_columns: 6,
                        grid_break: true,
                      },
                    },
                    features: {
                      type: 'array',
                      format: 'grid-strict',
                      title: 'leaflet_layer_features',
                      items: {
                        type: 'object',
                        format: 'grid-strict',
                        title: 'leaflet_layer_feature',
                        options: {
                          disable_collapse: true,
                        },
                        properties: {
                          type: {
                            title: 'leaflet_feature_type',
                            type: 'string',
                            enum: ['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'],
                            options: {
                              enum_titles: [
                                'Point',
                                'LineString',
                                'Polygon',
                                'MultiPoint',
                                'MultiLineString',
                                'MultiPolygon',
                              ],
                              select2_options: {
                                default: 'Point',
                                allowClear: false,
                              },
                              grid_columns: 6,
                            },
                          },
                          style: {
                            title: 'leaflet_feature_style',
                            type: 'string',
                            format: 'text',
                            options: {
                              grid_columns: 6,
                            },
                          },
                          popup: {
                            title: 'popup_url',
                            type: 'string',
                            enum: ['default', ...urlSource.map((value) => value.url)],
                            options: {
                              select2_options: {
                                placeholder: '',
                                allowClear: true,
                                default: 'default',
                              },
                              grid_columns: 6,
                            },
                          },
                          tooltip: {
                            title: 'leaflet_feature_tooltip',
                            type: 'string',
                            options: {
                              grid_columns: 6,
                            },
                          },
                          urlParams: {
                            title: 'popup_url_params',
                            type: 'string',
                            options: {
                              grid_columns: 6,
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              hiddenSearchData: {
                type: 'object',
                title: 'extra_filter',
                properties: {
                  where: {
                    type: 'array',
                    format: 'table',
                    title: 'conditions',
                    items: {
                      type: 'object',
                      title: 'condition',
                      properties: {
                        key: {
                          title: 'column_name',
                          type: 'string',
                        },
                        op: {
                          title: 'operation',
                          type: 'string',
                          enum: OPERATIONS.map((op) => op.value),
                          options: {
                            enum_titles: OPERATIONS.map((op) => op.key),
                            select2_options: {
                              default: '=',
                              allowClear: false,
                            },
                          },
                        },
                        value: {
                          title: 'value',
                          type: 'string',
                        },
                      },
                    },
                  },
                  logic: {
                    type: 'string',
                    title: 'logic',
                  },
                },
              },
            },
          },
        },
        options: {
          type: 'object',
          title: 'map_options',
          properties: {
            enableZoomOnScroll: {
              title: 'enable_zoom_on_scroll',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                streetView: {
                  title: 'street_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: true,
                },
                satelliteView: {
                  title: 'satellite_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                topoView: {
                  title: 'topo_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['streetView', 'satelliteView', 'topoView'],
                  default: 'gridView',
                  options: { grid_columns: 12 },
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
          },
        },
        bounds: {
          type: 'array',
          format: 'table',
          title: 'bounds',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'bounds',
            properties: {
              latitude: {
                title: 'latitude',
                type: 'string',
              },
              Longitude: {
                title: 'longitude',
                type: 'string',
              },
            },
          },
        },
      }
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.JSON_FORM: {
      const schema = {
        properties: {
          base: {
            properties: {
              formID: {
                title: 'JsonForm',
                type: 'string',
                enum: ['', ...jsonForms.map((value) => value.id)],
                options: {
                  enum_titles: ['', ...jsonForms.map((value) => value.title)],
                  select2_options: {
                    placeholder: '',
                    allowClear: true,
                  },
                },
                minLength: 1,
              },
            },
          },
        },
      }

      options.schema = deepmerge(options.schema, schema)

      break
    }
    case PANEL_TYPES.TEMPLATE_VIEWER: {
      const schema = {
        properties: {
          base: {
            properties: {
              form: {
                title: 'form',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'entity_name',
                    type: 'string',
                    enum: ['', ...mongoIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    // minLength: 1,
                  },
                  pk: {
                    title: 'pk',
                    type: 'string',
                    // default: 'null',
                    // minLength: 1,
                  },
                },
              },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.RELATIONSHIPS: {
      const schema = {
        properties: {
          base: {
            properties: {
              formID: {
                title: 'form',
                type: 'string',
                enum: ['', ...mongoIdSource.map((value) => value.value)],
                options: {
                  enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                  select2_options: {
                    placeholder: '',
                    allowClear: true,
                  },
                },
                minLength: 1,
              },
              usePopupDialog: {
                title: 'use_popup_dialog',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
              showParentDetails: {
                title: 'show_parent_details',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
              order: {
                type: 'array',
                format: 'table',
                title: 'relations_order',
                uniqueItems: true,
                items: {
                  type: 'object',
                  title: 'relations_order_overwrite',
                  properties: {
                    relationName: {
                      type: 'string',
                      title: 'related_table_name',
                      options: {
                        inputAttributes: {
                          placeholder: 'table name...',
                        },
                      },
                    },
                    seq: {
                      title: 'sequence',
                      type: 'integer',
                      minimum: 1,
                    },
                  },
                },
              },
              extraOptions: {
                type: 'object',
                title: 'list_extra_options',
                format: 'grid',
                options: {
                  collapsed: false,
                },
                properties: {
                  showGroupActionsButton: {
                    title: 'show_group_actions_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showSelectionColumn: {
                    title: 'show_checkbox_column',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showImportButtons: {
                    title: 'show_import_buttons',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showSavedFilter: {
                    title: 'show_saved_filter',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showResetButton: {
                    title: 'show_reset_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showPagination: {
                    title: 'show_pagination',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showPageLimiter: {
                    title: 'show_page_limiter',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showFilters: {
                    title: 'show_filter_bar',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showUniversalFilter: {
                    title: 'show_universal_filter_bar',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showLockColumn: {
                    title: 'show_lock_column',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showColumnSelector: {
                    title: 'show_column_selector',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showCreateDate: {
                    title: 'show_createdate_column',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showCreateUser: {
                    title: 'show_createuser_column',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showDetailView: {
                    title: 'show_detail_view',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showActionButtons: {
                    title: 'show_action_buttons',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  exportSettings: {
                    type: 'object',
                    title: 'list_export_settings',
                    format: 'grid',
                    properties: {
                      showExportButton: {
                        title: 'show_list_data_export_button',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 12 },
                        default: true,
                      },
                      exportTypes: {
                        title: 'exportable_filetypes',
                        type: 'object',
                        format: 'grid',
                        options: { disable_collapse: true },
                        properties: {
                          html: {
                            title: 'HTML',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 3 },
                            default: true,
                          },
                          csv: {
                            title: 'CSV',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 4 },
                            default: true,
                          },
                          txt: {
                            title: 'TXT',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 5 },
                            default: true,
                          },
                          pdf: {
                            title: 'PDF',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 3 },
                            default: true,
                          },
                          xlsx: {
                            title: 'Excel 2007 +',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 4 },
                            default: true,
                          },
                          ods: {
                            title: 'OpenDocument Spreadsheet',
                            type: 'boolean',
                            format: 'checkbox',
                            options: { grid_columns: 5 },
                            default: true,
                          },
                        },
                      },
                    },
                  },
                  columns: {
                    type: 'array',
                    format: 'table',
                    title: 'columns_order',
                    uniqueItems: true,
                    items: {
                      type: 'object',
                      title: 'column_overwrite',
                      properties: {
                        columnName: {
                          type: 'string',
                          title: 'column',
                          options: {
                            inputAttributes: {
                              placeholder: 'column name...',
                            },
                          },
                        },
                        label: {
                          title: 'label',
                          type: 'string',
                        },
                        seq: {
                          title: 'sequence',
                          type: 'integer',
                          minimum: 1,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.LIST_VIEW: {
      const schema = {
        properties: {
          base: {
            properties: {
              list: {
                title: 'list',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'form',
                    type: 'string',
                    enum: ['', ...mongoIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  showDetails: {
                    title: 'show_details',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  styleOptions: {
                    type: 'object',
                    format: 'table',
                    title: 'style_options',
                    properties: {
                      className: { title: 'classname', type: 'string' },
                    },
                  },
                  order: {
                    type: 'object',
                    format: 'table',
                    title: 'order',
                    properties: {
                      column: { title: 'column_name', type: 'string' },
                      dir: {
                        title: 'direction',
                        type: 'string',
                        enum: ['asc', 'desc'],
                      },
                    },
                  },
                  orderColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'order_columns',
                    items: {
                      type: 'string',
                      title: 'order_columns',
                      properties: {
                        name: { title: 'Name', type: 'string' },
                      },
                    },
                  },
                  exportColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'export_columns',
                    items: {
                      type: 'string',
                      title: 'add_column',
                      properties: {
                        name: { title: 'Name', type: 'string' },
                      },
                    },
                  },
                  exportAll: {
                    title: 'export_all',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  exportPage: {
                    title: 'export_page',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  hiddenSearchData: {
                    type: 'object',
                    title: 'extra_filter',
                    properties: {
                      where: {
                        type: 'array',
                        format: 'table',
                        title: 'conditions',
                        uniqueItems: true,
                        items: {
                          type: 'object',
                          title: 'condition',
                          properties: {
                            key: { title: 'column_name', type: 'string' },
                            op: {
                              title: 'operation',
                              type: 'string',
                              enum: OPERATIONS.map((op) => op.value),
                              options: {
                                enum_titles: OPERATIONS.map((op) => op.key),
                                select2_options: {
                                  default: '=',
                                  allowClear: false,
                                },
                              },
                            },
                            value: { title: 'value', type: 'string' },
                          },
                        },
                      },
                      logic: {
                        type: 'string',
                        title: 'logic',
                      },
                      filterExp: {
                        type: 'object',
                        title: 'filter_expression',
                        properties: {
                          table: { type: 'string', title: 'table_name' },
                          //                                  select: {type: 'array', title: 'Field name pl: ['ID_ALLASKERESO']'},
                          select: {
                            type: 'array',
                            format: 'table',
                            title: 'field_name_ex:_ID_FIELD',
                            items: {
                              type: 'string',
                              title: 'field_name',
                              properties: {
                                name: { title: 'name', type: 'string' },
                              },
                            },
                          },
                          field: { type: 'string', title: 'field_name' },
                          operation: {
                            title: 'operation',
                            type: 'string',
                            enum: ['', 'in', 'not in'],
                          },
                          where: {
                            type: 'array',
                            format: 'table',
                            title: 'conditions',
                            uniqueItems: true,
                            items: {
                              type: 'object',
                              title: 'condition',
                              properties: {
                                key: { title: 'column_name', type: 'string' },
                                op: {
                                  title: 'operation',
                                  type: 'string',
                                  enum: OPERATIONS.map((op) => op.value),
                                  options: {
                                    enum_titles: OPERATIONS.map((op) => op.key),
                                    select2_options: {
                                      default: '=',
                                      allowClear: false,
                                    },
                                  },
                                },
                                value: { title: 'value', type: 'string' },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                  extraOptions: {
                    type: 'object',
                    title: 'listview_extra_options',
                    format: 'grid',
                    properties: {
                      showCreateDate: {
                        title: 'show_CreateDate',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: true,
                      },
                      showCreateUser: {
                        title: 'show_CreateUser',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: false,
                      },
                    },
                  },
                },
              },
              pageSize: { title: 'page_size', type: 'number' },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  template: {
                    title: 'layout',
                    type: 'string',
                    minLength: 1,
                  },
                  emptyTemplate: {
                    title: 'empty_layout',
                    type: 'string',
                  },
                },
              },
              infiniteScroll: {
                title: 'use_infinite_scroll',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.CAROUSEL: {
      const schema = {
        properties: {
          base: {
            properties: {
              list: {
                title: 'carousel',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  queryId: {
                    title: 'query_name',
                    type: 'string',
                    enum: ['', ...queryIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...queryIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  styleOptions: {
                    type: 'object',
                    format: 'table',
                    title: 'options',
                    properties: {
                      className: { title: 'Wrapper classname', type: 'string', default: 'carousel' },
                      dots: {
                        title: 'dot_indicators',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: true,
                      },
                      arrows: {
                        title: 'arrows',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: true,
                      },
                      infinite: {
                        title: 'infinite_loop',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: true,
                      },
                      autoplay: {
                        title: 'autoplay',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 6 },
                        default: true,
                      },
                      autoplaySpeed: {
                        title: 'autoplay_speed_in_milliseconds',
                        type: 'number',
                        default: 3000,
                      },
                      speed: {
                        title: 'animation_speed_in_milliseconds',
                        type: 'number',
                        default: 500,
                      },
                      cssEase: {
                        title: 'css3_animation_easing',
                        type: 'string',
                        default: 'ease',
                      },
                      length: { title: 'page_size', type: 'number', default: 0 },
                      slidesToShow: {
                        title: 'number_of_slides_to_show',
                        type: 'number',
                        default: 3,
                      },
                      slidesToScroll: {
                        title: 'number_of_slides_to_scroll',
                        type: 'number',
                        default: 1,
                      },
                    },
                  },
                },
              },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  template: {
                    title: 'layout',
                    type: 'string',
                    minLength: 1,
                  },
                  emptyTemplate: {
                    title: 'empty_layout',
                    type: 'string',
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }

    case PANEL_TYPES.GANTT: {
      const schema = {
        properties: {
          base: {
            properties: {
              projectIdUrlParam: {
                type: 'string',
                title: 'project_id_url_param',
              },
              dynforms: {
                title: 'dynforms_mapper',
                type: 'object',
                format: 'table',
                properties: {
                  projectDynformId: {
                    title: 'project_dynform_id',
                    type: 'string',
                  },
                  taskDynformId: {
                    title: 'task_dynform_id',
                    type: 'string',
                  },
                  taskTypeDynformId: {
                    title: 'task_type_dynform_id',
                    type: 'string',
                  },
                  linkDynformId: {
                    title: 'link_dynform_id',
                    type: 'string',
                  },
                  linkTypeDynformId: {
                    title: 'link_type_dynform_id',
                    type: 'string',
                  },
                },
              },
              mappers: {
                title: 'field_mappers',
                type: 'object',
                format: 'table',
                properties: {
                  projectMapper: {
                    title: 'project_mapper',
                    type: 'object',
                    format: 'table',
                    properties: {
                      idColumn: {
                        title: 'id_column',
                        type: 'string',
                      },
                    },
                  },
                  taskMapper: {
                    title: 'task_mapper',
                    type: 'object',
                    format: 'table',
                    properties: {
                      idColumn: {
                        title: 'id_column',
                        type: 'string',
                      },
                      nameColumn: {
                        title: 'name_column',
                        type: 'string',
                      },
                      progressColumn: {
                        title: 'progress_column',
                        type: 'string',
                      },
                      startColumn: {
                        title: 'start_column',
                        type: 'string',
                      },
                      plannedStartColumn: {
                        title: 'planned_start_column',
                        type: 'string',
                      },
                      endColumn: {
                        title: 'end_column',
                        type: 'string',
                      },
                      plannedEndColumn: {
                        title: 'planned_end_column',
                        type: 'string',
                      },
                      parentIdColumn: {
                        title: 'parent_id_column',
                        type: 'string',
                      },
                      taskTypeIdColumn: {
                        title: 'task_type_id_column',
                        type: 'string',
                      },
                      additionalColumnMappings: {
                        title: 'additional_column_mappings',
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            columnName: {
                              title: 'column_name',
                              type: 'string',
                            },
                            label: {
                              title: 'label',
                              type: 'string',
                            },
                            editor: {
                              title: 'editor',
                              type: 'string',
                              default: 'none',
                              enum: ['none', 'text', 'number', 'date'],
                            },
                            minWidth: {
                              title: 'min_width',
                              type: 'number',
                            },
                          },
                        },
                      },
                    },
                  },
                  taskTypeMapper: {
                    title: 'task_type_mapper',
                    type: 'object',
                    format: 'table',
                    properties: {
                      idColumn: {
                        title: 'id_column',
                        type: 'string',
                      },
                      nameColumn: {
                        title: 'name_column',
                        type: 'string',
                      },
                      colorColumn: {
                        title: 'color_column',
                        type: 'string',
                      },
                    },
                  },
                  linkMapper: {
                    title: 'link_mapper',
                    type: 'object',
                    format: 'table',
                    properties: {
                      idColumn: {
                        title: 'id_column',
                        type: 'string',
                      },
                      sourceTaskIdColumn: {
                        title: 'source_task_id_column',
                        type: 'string',
                      },
                      targetTaskIdColumn: {
                        title: 'target_task_id_column',
                        type: 'string',
                      },
                      lagColumn: {
                        title: 'lag_column',
                        type: 'string',
                      },
                      linkTypeIdColumn: {
                        title: 'link_type_id_column',
                        type: 'string',
                      },
                    },
                  },
                  linkTypeMapper: {
                    title: 'link_type_mapper',
                    type: 'object',
                    format: 'table',
                    properties: {
                      idColumn: {
                        title: 'id_column',
                        type: 'string',
                      },
                      codeColumn: {
                        title: 'code_column',
                        type: 'string',
                      },
                      nameColumn: {
                        title: 'name_column',
                        type: 'string',
                      },
                    },
                  },
                },
              },
              filters: {
                title: 'task_filters',
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    columnName: {
                      title: 'column_name',
                      type: 'string',
                    },
                    operator: {
                      title: 'operator',
                      type: 'string',
                    },
                    value: {
                      title: 'value',
                      type: 'number',
                    },
                  },
                },
              },
              exportConfig: {
                title: 'export_config_mapper',
                type: 'array',
                maxItems: 7,
                items: {
                  type: 'object',
                  properties: {
                    fileType: {
                      title: 'file_type',
                      type: 'string',
                      enum: ['pdf', 'png', 'json', 'excel', 'ICal', 'MSProject', 'PrimaveraP6'],
                    },
                    enable: {
                      title: 'enable',
                      type: 'boolean',
                    },
                  },
                },
              },
              actionButtons: {
                title: 'action_buttons_mapper',
                type: 'object',
                properties: {
                  createEnabled: {
                    title: 'create_button',
                    type: 'boolean',
                  },
                  optionsEnabled: {
                    title: 'options_enabled',
                    type: 'boolean',
                  },
                  updateEnabled: {
                    title: 'update_enabled',
                    type: 'boolean',
                  },
                  deleteEnabled: {
                    title: 'delete_enabled',
                    type: 'boolean',
                  },
                },
              },
              defaultColumns: {
                title: 'default_columns',
                type: 'object',
                properties: {
                  indexColumn: {
                    title: 'index_column',
                    type: 'object',
                    properties: {
                      display: {
                        title: 'display_column',
                        type: 'boolean',
                      },
                      type: {
                        title: 'display_type',
                        type: 'string',
                        enum: ['wbs', 'index'],
                        nullable: true,
                      },
                    },
                  },
                  nameColumn: {
                    title: 'name_column',
                    type: 'object',
                    properties: {
                      display: {
                        title: 'display_column',
                        type: 'boolean',
                      },
                      editor: {
                        title: 'inline_edit',
                        type: 'boolean',
                      },
                    },
                  },
                  startDateColumn: {
                    title: 'start_date_column',
                    type: 'object',
                    properties: {
                      display: {
                        title: 'display_column',
                        type: 'boolean',
                      },
                      editor: {
                        title: 'inline_edit',
                        type: 'boolean',
                      },
                    },
                  },
                  endDateColumn: {
                    title: 'end_date_column',
                    type: 'object',
                    properties: {
                      inclusive: {
                        title: 'inclusive',
                        type: 'boolean',
                      },
                    },
                  },
                  durationColumn: {
                    title: 'duration_column',
                    type: 'object',
                    properties: {
                      display: {
                        title: 'display_column',
                        type: 'boolean',
                      },
                    },
                  },
                  plannedColumns: {
                    title: 'planned_columns',
                    type: 'object',
                    properties: {
                      display: {
                        title: 'display_bar',
                        type: 'boolean',
                      },
                      color: {
                        title: 'color',
                        type: 'string',
                      },
                    },
                  },
                },
              },
              scales: {
                title: 'scales_config',
                type: 'array',
                maxItems: 5,
                items: {
                  type: 'object',
                  properties: {
                    unit: {
                      title: 'unit',
                      type: 'string',
                      enum: ['day', 'week', 'month', 'quarter', 'year'],
                    },
                    step: {
                      title: 'step',
                      type: 'number',
                      const: 1,
                    },
                    format: {
                      title: 'format',
                      type: 'string',
                      enum: ['%d (%D)', '%m-%d', '%Y-%m-%d', '%Y'],
                    },
                  },
                },
              },
              ganttPlugins: {
                title: 'gantt_plugins',
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    plugin: {
                      title: 'gantt_plugin',
                      type: 'string',
                    },
                    enabled: {
                      title: 'enabled',
                      type: 'boolean',
                    },
                  },
                },
              },
              ganttConfig: {
                title: 'gantt_configs',
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    config: {
                      title: 'gantt_config',
                      type: 'string',
                    },
                    enabled: {
                      title: 'enabled',
                      type: 'boolean',
                    },
                  },
                },
              },

              extraOptions: {
                type: 'object',
                format: 'table',
                title: 'extra_options',
                properties: {
                  buttons: {
                    type: 'array',
                    format: 'grid-strict',
                    title: 'buttons_overwrite',
                    uniqueItems: true,
                    items: {
                      type: 'object',
                      format: 'grid-strict',
                      title: 'button_overwrite',
                      headerTemplate: '{{ self.name }} - {{ self.selector }}',
                      properties: {
                        selector: {
                          title: 'selector',
                          type: 'string',
                          options: { grid_columns: 6 },
                        },
                        name: {
                          title: 'label',
                          type: 'string',
                          options: { grid_columns: 4 },
                        },
                        seq: {
                          title: 'seq',
                          type: 'string',
                          options: { grid_columns: 2 },
                        },
                        url: {
                          title: 'URL',
                          type: 'string',
                          options: { grid_columns: 8 },
                        },
                        target: {
                          title: 'target',
                          type: 'string',
                          enum: ['_self', '_blank'],
                          options: { grid_columns: 4 },
                        },
                        buttonType: {
                          title: 'button_type',
                          type: 'string',
                          enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                          options: { grid_columns: 4 },
                        },
                        buttonStyle: {
                          title: 'style',
                          type: 'string',
                          options: { grid_columns: 4 },
                        },
                        icon: {
                          title: 'icon',
                          type: 'string',
                          options: { grid_columns: 4 },
                        },
                        hidden: {
                          title: 'hidden',
                          type: 'boolean',
                          format: 'checkbox',
                          default: false,
                          options: { grid_columns: 4 },
                        },
                        groupHidden: {
                          title: 'group_hidden',
                          type: 'boolean',
                          format: 'checkbox',
                          default: false,
                          options: { grid_columns: 4 },
                        },
                        popup: {
                          title: 'popup',
                          type: 'boolean',
                          format: 'checkbox',
                          default: false,
                          options: { grid_columns: 4 },
                        },
                      },
                    },
                  },
                },
              },

              defaultSort: {
                title: 'default_sort',
                type: 'string',
                enum: ['date', 'abc'],
              },
              defaultScale: {
                title: 'default_scale',
                type: 'string',
                enum: ['day', 'week', 'month', 'quarter', 'year'],
              },
              locale: {
                title: 'select_locale',
                type: 'string',
                default: 'hu',
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }

    case PANEL_TYPES.QUERY_LIST_VIEW: {
      const schema = {
        properties: {
          base: {
            properties: {
              list: {
                title: 'query_list',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  queryId: {
                    title: 'query_name',
                    type: 'string',
                    enum: ['', ...queryIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...queryIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  styleOptions: {
                    type: 'object',
                    format: 'table',
                    title: 'style_options',
                    properties: {
                      className: { title: 'classname', type: 'string' },
                    },
                  },

                  orderColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'order_columns',
                    items: {
                      type: 'object',
                      properties: {
                        name: { title: 'column_name', type: 'string' },
                      },
                    },
                  },
                  exportColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'export_columns',
                    items: {
                      type: 'object',
                      title: 'add_column',
                      properties: {
                        name: { title: 'column_name', type: 'string' },
                      },
                    },
                  },
                  exportAll: {
                    title: 'export_all',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  exportPage: {
                    title: 'export_page',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  exportLimit: {
                    title: 'limit_of_exportable_rows_(0_for_no_limit)',
                    type: 'number',
                    default: 100,
                  },
                  exportLimitInformation: {
                    title: 'limit_information_text',
                    type: 'string',
                    default: 'export_limit_notification',
                  },
                },
              },
              pageSize: { title: 'page_size', type: 'number' },
              start: { title: 'Start', type: 'number' },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  template: {
                    title: 'layout',
                    type: 'string',
                    minLength: 1,
                  },
                  emptyTemplate: {
                    title: 'empty_layout',
                    type: 'string',
                  },
                },
              },
              infiniteScroll: {
                title: 'use_infinite_scroll',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.PROCESS_LIST_VIEW: {
      const schema = {
        properties: {
          base: {
            properties: {
              pageSize: { title: 'page_size', type: 'number' },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  template: {
                    title: 'layout',
                    type: 'string',
                    minLength: 1,
                  },
                  emptyTemplate: {
                    title: 'empty_layout',
                    type: 'string',
                  },
                },
              },
              listSetupStatic: {
                type: 'string',
                format: 'textarea',
                title: 'list_setup',
              },
              infiniteScroll: {
                title: 'use_infinite_scroll',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
            },
            infiniteScroll: {
              title: 'use_infinite_scroll',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.TEMPLATE_VIEW: {
      const schema = {
        properties: {
          base: {
            properties: {
              template: {
                title: 'html',
                type: 'string',
                format: 'textarea',
              },
              css: {
                title: 'css',
                type: 'string',
                format: 'textarea',
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.LIST_FILTER: {
      const schema = {
        properties: {
          base: {
            properties: {
              list: {
                title: 'list',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'list_name',
                    type: 'string',
                    enum: ['', ...mongoIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                },
              },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                },
              },
              searchButton: {
                type: 'object',
                format: 'table',
                title: 'search_button',
                properties: {
                  label: { title: 'label', type: 'string' },
                },
              },
              clearButton: {
                type: 'object',
                format: 'table',
                title: 'clear_button',
                properties: {
                  label: { title: 'label', type: 'string' },
                },
              },
              styleOptions: {
                type: 'object',
                format: 'table',
                title: 'style_options',
                properties: {
                  className: { title: 'classname', type: 'string' },
                },
              },
              showSavedFilter: {
                title: 'show_saved_filter',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.QUERY_LIST_FILTER: {
      const schema = {
        properties: {
          base: {
            properties: {
              list: {
                title: 'query_list_filter',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  queryId: {
                    title: 'query_name',
                    type: 'string',
                    enum: ['', ...queryIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...queryIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                },
              },
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                },
              },
              searchButton: {
                type: 'object',
                format: 'table',
                title: 'search_button',
                properties: {
                  label: { title: 'label', type: 'string' },
                  visible: {
                    title: 'visible',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                },
              },
              clearButton: {
                type: 'object',
                format: 'table',
                title: 'clear_button',
                properties: {
                  label: { title: 'label', type: 'string' },
                  visible: {
                    title: 'visible',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
              styleOptions: {
                type: 'object',
                format: 'table',
                title: 'style_options',
                properties: {
                  className: { title: 'classname', type: 'string' },
                },
              },
              showSavedFilter: {
                title: 'show_saved_filter',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.PROCESS_LIST_FILTER: {
      const schema = {
        properties: {
          base: {
            properties: {
              template: {
                title: 'layout',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  id: {
                    title: 'layout_name',
                    type: 'number',
                    enum: ['', ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                  },
                },
              },
              searchButton: {
                type: 'object',
                format: 'table',
                title: 'search_button',
                properties: {
                  label: { title: 'label', type: 'string' },
                  visible: {
                    title: 'visible',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                },
              },
              styleOptions: {
                type: 'object',
                format: 'table',
                title: 'style_options',
                properties: {
                  className: { title: 'class_name', type: 'string' },
                },
              },
              showSavedFilter: {
                title: 'show_saved_filter',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
              clearParams: {
                title: 'use_only_own_search_params',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
              exportColumns: {
                title: 'export_columns',
                type: 'array',
                format: 'table',
                uniqueItems: true,
                options: {
                  collapsed: false,
                },
                items: {
                  type: 'object',
                  properties: {
                    name: {
                      title: 'column_name',
                      type: 'string',
                    },
                    key: {
                      title: 'column_key',
                      type: 'string',
                    },
                  },
                },
              },
              exportAll: {
                title: 'export_all',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
              exportPage: {
                title: 'export_page',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
              orderColumns: {
                title: 'order_columns',
                type: 'array',
                format: 'table',
                uniqueItems: true,
                options: {
                  collapsed: false,
                },
                items: {
                  type: 'object',
                  properties: {
                    name: {
                      title: 'column_name',
                      type: 'string',
                    },
                    key: {
                      title: 'column_key',
                      type: 'string',
                    },
                    isDefault: {
                      title: 'default',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                    },
                  },
                },
              },
              listSetupStatic: {
                type: 'string',
                format: 'textarea',
                title: 'list_setup',
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.FULL_CALENDAR_WRAPPER: {
      const schema = {
        properties: {
          base: {
            properties: {
              entityCollection: {
                title: 'entity_collection',
                type: 'array',
                items: {
                  title: 'entity',
                  type: 'object',
                  options: {
                    collapsed: false,
                  },
                  properties: {
                    mongoid: {
                      title: 'entity_name',
                      type: 'string',
                      enum: ['', ...mongoIdSource.map((value) => value.value)],
                      options: {
                        enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                        select2_options: {
                          placeholder: '',
                          allowClear: true,
                        },
                      },
                      minLength: 1,
                    },
                    startDate: {
                      title: 'start_date',
                      type: 'string',
                    },
                    endDate: {
                      title: 'end_date',
                      type: 'string',
                    },
                    alias: {
                      title: 'alias',
                      type: 'string',
                    },
                    displayField: {
                      title: 'display_field',
                      type: 'string',
                    },
                    controller: {
                      title: 'controller',
                      type: 'string',
                    },
                    href: {
                      title: 'redirect_url',
                      type: 'string',
                    },
                    createNewElement: {
                      title: 'create_new_element',
                      type: 'boolean',
                      format: 'checkbox',
                      default: true,
                    },
                    styleOptions: {
                      title: 'style_options',
                      type: 'object',
                      properties: {
                        color: {
                          title: 'color',
                          type: 'string',
                          format: 'color',
                          default: '#86cb76',
                        },
                        customClasses: {
                          type: 'array',
                          format: 'table',
                          title: 'custom_classes',
                          options: { collapsed: false },
                          items: {
                            type: 'object',
                            title: 'condition',
                            properties: {
                              exp: { title: 'custom_class_exp', type: 'string' },
                              class: {
                                title: 'custom_class_name',
                                type: 'string',
                              },
                            },
                          },
                        },
                      },
                    },
                    parameters: {
                      type: 'array',
                      format: 'table',
                      title: 'parameters',
                      uniqueItems: true,
                      options: {
                        collapsed: true,
                      },
                      items: {
                        type: 'object',
                        title: 'parameter',
                        properties: {
                          key: { title: 'column_name', type: 'string' },
                          value: { title: 'value', type: 'string' },
                        },
                      },
                    },
                    hiddenSearch: {
                      type: 'object',
                      title: 'extra_filter',
                      properties: {
                        where: {
                          type: 'array',
                          format: 'table',
                          title: 'conditions',
                          uniqueItems: true,
                          options: {
                            collapsed: false,
                          },
                          items: {
                            type: 'object',
                            title: 'condition',
                            properties: {
                              key: { title: 'column_name', type: 'string' },
                              op: {
                                title: 'operation',
                                type: 'string',
                                enum: OPERATIONS.map((op) => op.value),
                                options: {
                                  enum_titles: OPERATIONS.map((op) => op.key),
                                  select2_options: {
                                    default: '',
                                    allowClear: false,
                                  },
                                },
                              },
                              value: { title: 'value', type: 'string' },
                            },
                          },
                        },
                        logic: {
                          type: 'string',
                          title: 'logic',
                        },
                      },
                    },
                  },
                },
              },
              calendarOptions: {
                title: 'calendar_options',
                type: 'object',
                format: 'grid',
                properties: {
                  defaultView: {
                    title: 'default_view',
                    type: 'string',
                    enum: ['month', 'agendaWeek', 'agendaDay', 'listWeek'],
                    default: 'month',
                    options: { grid_columns: 6 },
                  },
                  selectable: {
                    title: 'selectable',
                    type: 'boolean',
                    default: true,
                    options: { grid_columns: 6 },
                  },
                  isPopup: {
                    title: 'popup',
                    type: 'boolean',
                    default: true,
                    options: { grid_columns: 6 },
                  },
                  editable: {
                    title: 'editable',
                    type: 'boolean',
                    default: true,
                    options: { grid_columns: 6 },
                  },
                  entityPickerVisibility: {
                    title: 'entity_picker_visible',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 3 },
                  },
                  entityPickerEditable: {
                    title: 'entity_picker_editable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 3 },
                  },
                  displayEventEnd: {
                    title: 'display_event_end',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                    options: { grid_columns: 3 },
                  },
                  allowOverlap: {
                    title: 'allow_event_overlap',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                    options: { grid_columns: 3 },
                  },
                  minTime: {
                    title: 'display_first_time',
                    type: 'string',
                    default: '00:00:00',
                  },
                  maxTime: {
                    title: 'display_last_time',
                    type: 'string',
                    default: '24:00:00',
                  },
                  header: {
                    title: 'header',
                    type: 'object',
                    properties: {
                      left: {
                        title: 'left',
                        type: 'string',
                        default: 'prevYear, prev, today, next, nextYear',
                      },
                      center: {
                        title: 'center',
                        type: 'string',
                        default: 'title',
                      },
                      right: {
                        title: 'right',
                        type: 'string',
                        default: 'month, agendaWeek, agendaDay, listWeek',
                      },
                    },
                  },
                  footer: {
                    title: 'footer',
                    type: 'object',
                    properties: {
                      left: {
                        title: 'left',
                        type: 'string',
                        default: '',
                      },
                      center: {
                        title: 'center',
                        type: 'string',
                        default: '',
                      },
                      right: {
                        title: 'right',
                        type: 'string',
                        default: '',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.KANBAN_WRAPPER: {
      const schema = {
        properties: {
          base: {
            properties: {
              entityCollection: {
                title: 'entity_collection',
                type: 'object',
                properties: {
                  mongoId: {
                    title: 'ID',
                    type: 'string',
                    enum: ['', ...mongoIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  fieldId: {
                    title: 'column_field_name',
                    type: 'string',
                  },
                  itemId: {
                    title: 'Save field name',
                    type: 'string',
                  },
                  styleOptions: {
                    title: 'style_options',
                    type: 'object',
                    properties: {
                      color: {
                        title: 'color',
                        type: 'string',
                        format: 'color',
                        default: '#86cb76',
                      },
                    },
                  },
                  hiddenSearch: {
                    type: 'object',
                    title: 'extra_filter',
                    properties: {
                      where: {
                        type: 'array',
                        format: 'table',
                        title: 'conditions',
                        uniqueItems: true,
                        items: {
                          type: 'object',
                          title: 'condition',
                          properties: {
                            key: { title: 'column_name', type: 'string' },
                            op: {
                              title: 'operation',
                              type: 'string',
                              enum: OPERATIONS.map((op) => op.value),
                              options: {
                                enum_titles: OPERATIONS.map((op) => op.key),
                                select2_options: {
                                  default: '=',
                                  allowClear: false,
                                },
                              },
                            },
                            value: { title: 'value', type: 'string' },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.TASK_BOARD: {
      const schema = {
        properties: {
          base: {
            properties: {
              entityCollection: {
                title: 'entity_collection',
                type: 'object',
                properties: {
                  mongoId: {
                    title: 'ID',
                    type: 'string',
                    enum: ['', ...mongoIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  fieldId: {
                    title: 'column_field_name',
                    type: 'string',
                  },
                  addCardPosition: {
                    title: 'add card position',
                    type: 'string',
                    enum: ['under-list', 'under-column', 'both'],
                    options: {
                      enum_titles: ['under list', 'under column', 'both'],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 1,
                  },
                  showCreateButton: {
                    title: 'show_create_button',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  isPopup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  controllerName: {
                    title: 'controller_name',
                    type: 'string',
                  },
                  hiddenSearch: {
                    type: 'object',
                    title: 'extra_filter',
                    properties: {
                      uniqeFields: {
                        type: 'array',
                        format: 'table',
                        title: 'fields',
                        uniqueItems: true,
                        items: {
                          type: 'object',
                          title: 'field_customization',
                          properties: {
                            fieldType: {
                              title: 'task_field',
                              type: 'string',
                              enum: [
                                'Priority',
                                'Deadline',
                                'Task title',
                                'Task ID',
                                'Epic name',
                                'Description',
                                'Basic data field',
                                'Assigned User',
                              ],
                            },
                            fieldName: { title: 'field_reference', type: 'string' },
                          },
                        },
                      },
                      where: {
                        type: 'array',
                        format: 'table',
                        title: 'conditions',
                        uniqueItems: true,
                        items: {
                          type: 'object',
                          title: 'condition',
                          properties: {
                            key: { title: 'column_name', type: 'string' },
                            op: {
                              title: 'operation',
                              type: 'string',
                              enum: OPERATIONS.map((op) => op.value),
                              options: {
                                enum_titles: OPERATIONS.map((op) => op.key),
                                select2_options: {
                                  default: '=',
                                  allowClear: false,
                                },
                              },
                            },
                            value: { title: 'value', type: 'string' },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.STATUS_LINE: {
      const schema = {
        properties: {
          base: {
            properties: {
              idForm: {
                title: 'form',
                type: 'string',
                enum: ['', ...mongoIdSource.map((value) => value.value)],
                options: {
                  enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                  select2_options: {
                    placeholder: '',
                    allowClear: true,
                  },
                },
                minLength: 1,
              },
              showAllSteps: {
                title: 'show_all_steps_(checked),_or_just_the_current_process',
                type: 'boolean',
                format: 'checkbox',
                default: true,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.DOCUMENT_LIST: {
      const schema = {
        properties: {
          base: {
            properties: {
              docMTNList: {
                title: 'document_MTN_list',
                type: 'array',
                format: 'table',
                options: {
                  collapsed: false,
                },
                items: {
                  title: 'form',
                  type: 'string',
                  enum: ['', ...mongoIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.CUSTOM_COMPONENT: {
      const schema = {
        properties: {
          base: {
            properties: {
              componentName: {
                title: 'component_name',
                type: 'string',
                enum: ['', ...customComponents.map((value) => value.title), ...systemComponents],
              },
              componentProps: {
                title: 'component_properties',
                type: 'string',
                format: 'textarea',
              },
              namespace: {
                title: 'namespace',
                type: 'string',
              },
              infiniteScroll: {
                title: 'use_infinite_scroll',
                type: 'boolean',
                format: 'checkbox',
                default: false,
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.CHART_JS: {
      const specifiedOptions = {
        query: {
          title: 'query',
          type: 'string',
          enum: ['', ...queryIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...queryIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
        },
        process: {
          title: 'process',
          type: 'string',
          enum: ['', ...processIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...processIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
        },
        sql: {
          title: 'sql',
          type: 'string',
          format: 'textarea',
        },
        type: {
          title: 'chart_type',
          type: 'string',
          enum: [
            'BarChart',
            'HorizontalBarChart',
            'LineChart',
            'RadarChart',
            'PieChart',
            'DoughnutChart',
            'PolarAreaChart',
          ],
        },
        seriesProperties: {
          title: 'series_properties',
          properties: {
            columnName: {
              title: 'column_name_for_label',
              type: 'string',
            },
            linkExpression: {
              title: 'dynamic_link_expression',
              type: 'string',
            },
            columnBorderWidth: {
              title: 'column_name_for_border_width',
              type: 'string',
            },
            hiddenColumns: {
              title: 'hidden_columns',
              type: 'array',
              format: 'table',
              items: {
                title: 'property_name',
                type: 'string',
                uniqueItems: true,
              },
              options: {
                collapsed: false,
              },
            },
          },
        },
        options: {
          type: 'object',
          title: 'chart_properties',
          properties: {
            data: {
              title: 'data_properties',
              properties: {
                labels: {
                  title: 'categories',
                  type: 'array',
                  format: 'table',
                  items: {
                    title: 'category_name',
                    type: 'string',
                  },
                  options: {
                    collapsed: false,
                  },
                },
              },
            },
            visuals: {
              title: 'data_visuals',
              type: 'array',
              options: {
                collapsed: false,
              },
              format: 'array',
              uniqueItems: true,
              items: {
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  backgroundColor: {
                    title: 'background_color',
                    type: 'string',
                    format: 'color',
                    default: '#86cb76',
                  },
                  borderColor: {
                    title: 'border_color',
                    type: 'string',
                    format: 'color',
                    default: '#86cb76',
                  },
                  width: {
                    title: 'border_width',
                    type: 'number',
                    default: 1,
                  },
                },
              },
            },

            options: {
              type: 'object',
              title: 'chart_options',
              properties: {
                title: {
                  title: 'title',
                  properties: {
                    display: {
                      title: 'show_title',
                      type: 'boolean',
                      default: true,
                    },
                    text: {
                      title: 'title_text',
                      type: 'string',
                    },
                  },
                },
                legend: {
                  type: 'object',
                  title: 'legend',
                  properties: {
                    display: {
                      title: 'show_legend',
                      type: 'boolean',
                      default: true,
                    },
                    reverse: {
                      title: 'reverse_order',
                      type: 'boolean',
                      default: false,
                    },
                    align: {
                      title: 'align_legend',
                      type: 'string',
                      enum: ['start', 'center', 'end'],
                      default: 'center',
                    },
                  },
                },
                maintainAspectRatio: {
                  title: 'maintain_aspect_ratio',
                  type: 'boolean',
                  default: false,
                },

                scales: {
                  title: 'scales',
                  type: 'object',
                  properties: {
                    yAxes: {
                      title: 'y_axes',
                      type: 'array',
                      items: {
                        type: 'object',
                        title: 'y_axe',
                        format: 'table',
                        options: {
                          collapsed: false,
                        },
                        properties: {
                          display: {
                            title: 'display',
                            type: 'boolean',
                            default: true,
                          },
                          scaleLabel: {
                            type: 'object',
                            title: 'scaleLabel',
                            format: 'table',
                            options: {
                              collapsed: false,
                            },
                            properties: {
                              display: {
                                title: 'display',
                                type: 'boolean',
                                default: true,
                              },
                              labelString: {
                                title: 'labelString',
                                type: 'string',
                              },
                            },
                          },
                          stacked: {
                            title: 'stacked',
                            type: 'boolean',
                            default: false,
                          },
                          ticks: {
                            title: 'display',
                            type: 'object',
                            properties: {
                              beginAtZero: {
                                title: 'begin_at_zero',
                                type: 'boolean',
                                default: true,
                              },
                              stepSize: {
                                title: 'step_size',
                                type: 'number',
                                default: 1,
                              },
                              callbackObject: {
                                title: 'callback_for_scale_values',
                                type: 'object',
                                properties: {
                                  args: {
                                    type: 'string',
                                    title: 'arguments',
                                  },
                                  body: {
                                    type: 'string',
                                    title: 'function_body',
                                  },
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                    xAxes: {
                      title: 'x_axes',
                      type: 'array',
                      items: {
                        type: 'object',
                        title: 'x_axe',
                        format: 'table',
                        options: {
                          collapsed: false,
                        },
                        properties: {
                          display: {
                            title: 'display',
                            type: 'boolean',
                            default: true,
                          },
                          scaleLabel: {
                            type: 'object',
                            title: 'scaleLabel',
                            format: 'table',
                            options: {
                              collapsed: false,
                            },
                            properties: {
                              display: {
                                title: 'display',
                                type: 'boolean',
                                default: true,
                              },
                              labelString: {
                                title: 'labelString',
                                type: 'string',
                              },
                            },
                          },
                          stacked: {
                            title: 'stacked',
                            type: 'boolean',
                            default: false,
                          },
                          ticks: {
                            title: 'display',
                            type: 'object',
                            properties: {
                              beginAtZero: {
                                title: 'begin_at_zero',
                                type: 'boolean',
                                default: true,
                              },
                              stepSize: {
                                title: 'step_size',
                                type: 'number',
                                default: 10,
                              },
                              callbackObject: {
                                title: 'callback_for_scale_values',
                                type: 'object',
                                properties: {
                                  args: {
                                    type: 'string',
                                    title: 'arguments',
                                  },
                                  body: {
                                    type: 'string',
                                    title: 'function_body',
                                  },
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
                tooltips: {
                  type: 'object',
                  title: 'Tooltips',
                  properties: {
                    enabled: {
                      type: 'boolean',
                      default: true,
                    },
                    tooltipCallbacks: {
                      type: 'array',
                      title: 'callbacks_for_customizing_tooltips',
                      items: {
                        type: 'object',
                        format: 'table',
                        properties: {
                          prop: {
                            type: 'string',
                            title: 'property_name',
                          },
                          args: {
                            type: 'string',
                            title: 'arguments',
                            default: 'tooltipItem, data',
                          },
                          body: {
                            type: 'string',
                            title: 'function_body',
                          },
                        },
                      },
                    },
                  },
                },
                plugins: {
                  type: 'object',
                  title: 'plugins',
                  properties: {
                    colorschemes: {
                      title: 'color_schema',
                      properties: {
                        scheme: {
                          title: 'Schema name (https://nagix.github.io/chartjs-plugin-colorschemes/colorchart.html)',
                          type: 'string',
                          default: 'brewer.Paired12',
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }

      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.ENVIRONMENT_MANAGER: {
      const specifiedOptions = {
        showSaveButton: {
          title: 'Show save button',
          type: 'boolean',
          format: 'checkbox',
          default: true,
        },
        showAddNewButton: {
          title: 'Show add new config button',
          type: 'boolean',
          format: 'checkbox',
          default: true,
        },
        properties: {
          type: 'array',
          format: 'table',
          title: 'Properties',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'Visible property',
            properties: {
              name: {
                type: 'string',
                title: 'Name',
                options: {
                  inputAttributes: {
                    placeholder: 'property name...',
                  },
                },
              },
            },
          },
        },
      }
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.FORM_DESIGNER: {
      const specifiedOptions = FormDesignerOptions
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.FORM_DESIGNER_OLD: {
      const specifiedOptions = FormDesignerOptions
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    case PANEL_TYPES.MENU_PANEL: {
      const specifiedOptions = {
        view: {
          title: 'menu_panel_view',
          type: 'string',
          enum: ['menu', 'buttons'],
          default: 'buttons',
        },
        menu: {
          title: 'select_menu',
          type: 'string',
          enum: ['', ...menus.map((value) => value.value)],
          options: {
            enum_titles: ['', ...menus.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
        },
      }
      options.schema.properties.base.properties = specifiedOptions
      break
    }
    case PANEL_TYPES.RAW_SQL_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          options: {
            collapsed: false,
          },
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            isColumnWidthEditable: {
              title: 'column_width_editable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            isSearchable: {
              title: 'is_list_searchable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showColumnSelector: {
              title: 'show_column_selector',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showStaticBinds: {
              title: 'show_static_binds',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showDynamicBinds: {
              title: 'show_dynamic_binds',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchBarIsCollapsed: {
              title: 'searchbar_is_collapsed',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            availableViews: {
              type: 'object',
              title: 'available_views',
              format: 'grid',
              properties: {
                gridView: {
                  title: 'grid_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: true,
                },
                cardView: {
                  title: 'card_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                detailView: {
                  title: 'detail_view',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 4 },
                  default: false,
                },
                defaultView: {
                  title: 'default_view',
                  type: 'string',
                  enum: ['gridView', 'cardView', 'detailView'],
                  default: 'gridView',
                  options: { grid_columns: 12 },
                  validation: function ({ value, path, editorValue }) {
                    const jsonPath = path
                      .split('.')
                      .slice(1, path.split('.').length - 1)
                      .join('.')
                    const parent = get(editorValue, jsonPath)
                    return {
                      property: 'Default view',
                      result: parent[value],
                      errorMessage: 'Default view must be chosen from enabled views',
                    }
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            dbClickRedirect: {
              title: 'doubleclick_redirect',
              type: 'object',
              format: 'grid',
              uniqueItems: true,
              properties: {
                active: {
                  title: 'active',
                  type: 'boolean',
                  format: 'checkbox',
                },
                popup: {
                  title: 'popup',
                  type: 'boolean',
                  format: 'checkbox',
                },
                url: {
                  title: 'URL',
                  type: 'string',
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  type: {
                    title: 'type',
                    type: 'string',
                    enum: ['text', 'number', 'date'],
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  showInList: {
                    title: 'show_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInList: {
                    title: 'available_in_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  availableInDetailView: {
                    title: 'available_in_detail_view',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                    default: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'array',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'order',
            properties: {
              column: { title: 'column_name', type: 'string' },
              dir: {
                title: 'direction',
                type: 'string',
                enum: ['asc', 'desc'],
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        rawSqlId: {
          type: 'string',
          title: 'raw_sql_name',
          enum: ['', ...rawSqlIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...rawSqlIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        customBindLayout: {
          title: 'custom_bind_layout',
          type: 'string',
          format: 'textarea',
        },
        customListLayout: {
          title: 'custom_list_layout',
          type: 'string',
          format: 'textarea',
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                rawSqlId: {
                  type: 'string',
                  title: 'list',
                  enum: ['', ...rawSqlIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...rawSqlIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.BUTTON_LIST: {
      const schema = {
        properties: {
          base: {
            properties: {
              entity: {
                type: 'string',
                title: 'entity',
                enum: ['', ...mongoIdSource.map((value) => camelCase(value.tableName))],
                options: {
                  enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                  select2_options: {
                    placeholder: '',
                    allowClear: true,
                  },
                },
                minLength: 1,
                propertyOrder: 0,
              },
              viewType: {
                title: 'view_type',
                type: 'string',
                enum: ['horizontal', 'vertical', 'floating'],
                default: 'horizontal',
              },
              containerType: {
                title: 'container_type',
                type: 'string',
                enum: ['wrap', 'scroll'],
                default: 'wrap',
              },
              buttonSize: {
                title: 'button_size',
                type: 'string',
                enum: ['default', 'medium', 'small', 'mini'],
                default: 'default',
              },
              floatingButtonPosition: {
                title: 'floating_button_position',
                type: 'string',
                enum: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
                default: 'bottom-right',
              },
              buttons: {
                type: 'array',
                format: 'grid-strict',
                title: 'buttons',
                uniqueItems: true,
                items: {
                  type: 'object',
                  format: 'grid-strict',
                  title: 'button',
                  headerTemplate: '{{ self.name }} - {{ self.selector }}',
                  properties: {
                    name: {
                      title: 'label',
                      type: 'string',
                      options: { grid_columns: 5 },
                      default: 'button',
                    },
                    selector: {
                      title: 'selector',
                      type: 'string',
                      options: { grid_columns: 4 },
                    },
                    seq: {
                      title: 'seq',
                      type: 'string',
                      options: { grid_columns: 3 },
                    },
                    url: {
                      title: 'URL',
                      type: 'string',
                      options: { grid_columns: 5 },
                    },
                    icon: {
                      title: 'icon_material_ui',
                      type: 'string',
                      options: { grid_columns: 4 },
                    },
                    buttonType: {
                      title: 'button_type',
                      type: 'string',
                      enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                      options: { grid_columns: 3 },
                    },
                    openInNewWindow: {
                      title: 'open_in_new_window',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 5 },
                    },
                    hidden: {
                      title: 'hidden',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 4 },
                    },
                    popup: {
                      title: 'popup',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 3 },
                    },
                    buttonStyle: {
                      title: 'style',
                      type: 'array',
                      format: 'grid-strict',
                      options: { grid_columns: 12, collapsed: true },
                      items: {
                        type: 'object',
                        format: 'grid-strict',
                        title: 'button_style',
                        headerTemplate: '{{ self.key }} : {{ self.value }}',
                        properties: {
                          key: {
                            title: 'key',
                            type: 'string',
                            options: { grid_columns: 6 },
                          },
                          value: {
                            title: 'value',
                            type: 'string',
                            options: { grid_columns: 6 },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.EDITABLE_LIST: {
      const specifiedOptions = {
        defaultEmpty: {
          title: 'default_empty_list',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        extraOptions: {
          options: {
            collapsed: false,
          },
          type: 'object',
          title: 'extra_options',
          format: 'grid',
          properties: {
            isColumnWidthEditable: {
              title: 'column_width_editable',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showGroupActionsButton: {
              title: 'show_group_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            runGroupActionsOnAllWithoutSelect: {
              title: 'run_group_actions_on_all_without_select',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showQueueActionsButton: {
              title: 'show_queue_actions_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSelectionColumn: {
              title: 'show_checkbox_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showImportButtons: {
              title: 'show_import_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            exportOriginalType: {
              title: 'export_list_in_original_type',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showSavedFilter: {
              title: 'show_saved_filter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showResetButton: {
              title: 'show_reset_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPagination: {
              title: 'show_pagination',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showPageLimiter: {
              title: 'show_page_limiter',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFilters: {
              title: 'show_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showUniversalFilter: {
              title: 'show_universal_filter_bar',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchKeypress: {
              title: 'show_search_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showLockColumn: {
              title: 'show_lock_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateDate: {
              title: 'show_createdate_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCreateUser: {
              title: 'show_createuser_column',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showDetailView: {
              title: 'show_detail_view',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showRelations: {
              title: 'show_relations',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            showActionButtons: {
              title: 'show_action_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showFooterButtons: {
              title: 'show_footer_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showAddRowButton: {
              title: 'show_add_row_button',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            showCollapsedActions: {
              title: 'show_collapsed_buttons',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: true,
            },
            summaryRowDefault: {
              title: 'summary_row_default',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            editOnDoubleClick: {
              title: 'edit_on_double_click',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            searchBarIsCollapsed: {
              title: 'searchbar_is_collapsed',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            filterNullEnabled: {
              title: 'filter_null_enabled',
              type: 'boolean',
              format: 'checkbox',
              options: { grid_columns: 6 },
              default: false,
            },
            actionButtonsStyle: {
              title: 'action_buttons_style',
              type: 'string',
              options: { grid_columns: 12 },
              enum: ['Inline', 'Not-Inline'],
              default: 'Not-Inline',
            },
            editUrl: {
              title: 'record_edit_url',
              options: { grid_columns: 12 },
              type: 'string',
            },
            exportSettings: {
              type: 'object',
              title: 'list_export_settings',
              format: 'grid',
              properties: {
                showExportButton: {
                  title: 'show_list_data_export_button',
                  type: 'boolean',
                  format: 'checkbox',
                  options: { grid_columns: 12 },
                  default: true,
                },
                exportTypes: {
                  title: 'exportable_filetypes',
                  type: 'object',
                  format: 'grid',
                  options: { disable_collapse: true },
                  properties: {
                    html: {
                      title: 'HTML',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    csv: {
                      title: 'CSV',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    txt: {
                      title: 'TXT',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                    pdf: {
                      title: 'PDF',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 3 },
                      default: true,
                    },
                    xlsx: {
                      title: 'Excel 2007 +',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 4 },
                      default: true,
                    },
                    ods: {
                      title: 'OpenDocument Spreadsheet',
                      type: 'boolean',
                      format: 'checkbox',
                      options: { grid_columns: 5 },
                      default: true,
                    },
                  },
                },
              },
            },
            buttons: {
              type: 'array',
              format: 'grid-strict',
              title: 'buttons_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                format: 'grid-strict',
                title: 'button_overwrite',
                headerTemplate: '{{ self.name }} - {{ self.selector }}',
                properties: {
                  selector: {
                    title: 'selector',
                    type: 'string',
                    options: { grid_columns: 6 },
                  },
                  name: {
                    title: 'label',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  seq: {
                    title: 'seq',
                    type: 'string',
                    options: { grid_columns: 2 },
                  },
                  url: {
                    title: 'URL',
                    type: 'string',
                    options: { grid_columns: 8 },
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                    options: { grid_columns: 4 },
                  },
                  buttonType: {
                    title: 'button_type',
                    type: 'string',
                    enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                    options: { grid_columns: 4 },
                  },
                  buttonStyle: {
                    title: 'style',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  icon: {
                    title: 'icon',
                    type: 'string',
                    options: { grid_columns: 4 },
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  groupHidden: {
                    title: 'group_hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                    options: { grid_columns: 4 },
                  },
                },
              },
            },
            columns: {
              type: 'array',
              format: 'table',
              title: 'columns_overwrite',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  label: {
                    title: 'label',
                    type: 'string',
                  },
                  aggregation: {
                    title: 'aggregation',
                    type: 'string',
                    enum: ['Off', 'SUM', 'MAX', 'AVG', 'MIN'],
                    default: 'Off',
                  },
                  hidden: {
                    title: 'hidden',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  exportable: {
                    title: 'exportable',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  seq: {
                    title: 'sequence',
                    type: 'integer',
                    minimum: 1,
                    default: 1,
                  },
                  pattern: {
                    title: 'without_pattern',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
            },
            columnRedirect: {
              type: 'array',
              format: 'table',
              title: 'columns_redirect',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'column_overwrite',
                properties: {
                  columnName: {
                    type: 'string',
                    title: 'column',
                    options: {
                      inputAttributes: {
                        placeholder: 'column name...',
                      },
                    },
                  },
                  popup: {
                    title: 'popup',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  target: {
                    title: 'target',
                    type: 'string',
                    enum: ['_self', '_blank'],
                  },
                },
              },
            },
            rowStyle: {
              type: 'array',
              format: 'table',
              title: 'row_styles',
              uniqueItems: true,
              items: {
                type: 'object',
                title: 'row_style',
                properties: {
                  selector: {
                    title: 'selector_expression',
                    type: 'string',
                  },
                  class: {
                    title: 'class',
                    type: 'string',
                  },
                  style: {
                    title: 'style',
                    type: 'string',
                  },
                  applied_to: {
                    title: 'applied_to',
                    type: 'string',
                    enum: ROW_STYLE_APPLIED_TO,
                    default: ROW_STYLE_DEFAULT_APPLIED_TO,
                  },
                },
              },
            },
          },
        },
        order: {
          type: 'array',
          format: 'table',
          title: 'order',
          uniqueItems: true,
          items: {
            type: 'object',
            title: 'order',
            properties: {
              column: { title: 'column_name', type: 'string' },
              dir: {
                title: 'direction',
                type: 'string',
                enum: ['asc', 'desc'],
              },
            },
          },
        },
        pageSize: { title: 'page_size', type: 'number' },
        infiniteScroll: {
          title: 'use_infinite_scroll',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        hiddenSearchData: {
          type: 'object',
          title: 'extra_filter',
          properties: {
            where: {
              type: 'array',
              format: 'table',
              title: 'conditions',
              items: {
                type: 'object',
                title: 'condition',
                properties: {
                  key: { title: 'column_name', type: 'string' },
                  op: { title: 'operation', type: 'string' },
                  value: { title: 'value', type: 'string' },
                },
              },
            },
            logic: {
              type: 'string',
              title: 'logic',
            },
            filterExp: {
              type: 'object',
              title: 'filter_expression',
              properties: {
                table: { type: 'string', title: 'table_name' },
                select: {
                  type: 'array',
                  format: 'table',
                  title: 'field_name_ex:_ID_ALLASKERESO',
                  items: {
                    type: 'string',
                    title: 'field_name',
                    properties: {
                      name: { title: 'Name', type: 'string' },
                    },
                  },
                },
                field: { type: 'string', title: 'field_name' },
                operation: {
                  title: 'operation',
                  type: 'string',
                  enum: ['', 'in', 'not in'],
                },
                where: {
                  type: 'array',
                  format: 'table',
                  title: 'conditions',
                  uniqueItems: true,
                  items: {
                    type: 'object',
                    title: 'condition',
                    properties: {
                      key: { title: 'column_name', type: 'string' },
                      op: { title: 'operation', type: 'string' },
                      value: { title: 'value', type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
        mongoId: {
          type: 'string',
          title: 'list',
          enum: ['', ...mongoIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
        controllerName: {
          title: 'controller_name',
          type: 'string',
        },
        subBlockUrl: {
          title: 'sub_block_url',
          type: 'string',
        },
        customLayout: {
          title: 'custom_layout',
          type: 'string',
          format: 'textarea',
        },
      }
      const specifiedOptionsCollapsed = {
        params: {
          type: 'object',
          title: 'params',
          properties: {
            base: {
              type: 'object',
              title: 'base',
              properties: {
                mongoId: {
                  type: 'string',
                  title: 'Mongo Id',
                  enum: ['', ...mongoIdSource.map((value) => value.value)],
                  options: {
                    enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
                    select2_options: {
                      placeholder: '',
                      allowClear: true,
                      dropdownAutoWidth: true,
                      width: '300px',
                    },
                  },
                  propertyOrder: 0,
                },
              },
            },
          },
        },
      }

      options.schema.properties.collapsed.properties = deepmerge(
        options.schema.properties.collapsed.properties,
        specifiedOptionsCollapsed
      )
      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)

      break
    }
    case PANEL_TYPES.TEMPLATED_LIST: {
      const schema = {
        properties: {
          base: {
            properties: {
              namespace: {
                title: 'store_namespace',
                type: 'string',
              },
              listSettings: {
                title: 'templated_list',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  sourceType: {
                    title: 'source_type',
                    type: 'string',
                    enum: ['form', 'query', 'process'],
                    options: {
                      enum_titles: ['form', 'query', 'process'],
                    },
                  },
                  idSource: {
                    title: 'source_id',
                    type: 'string',
                    enum: {
                      reference: 'base.listSettings.sourceType',
                      runner: (source) => {
                        switch (source) {
                          case SOURCE_TYPES.QUERY:
                            return ['', ...queryIdSource.map((value) => value.value)]
                          default:
                            return ['', ...mongoIdSource.map((value) => value.value)]
                        }
                      },
                    },
                    options: {
                      enum_titles: {
                        reference: 'base.listSettings.sourceType',
                        runner: (source) => {
                          switch (source) {
                            case SOURCE_TYPES.QUERY:
                              return ['', ...queryIdSource.map((value) => value.title)]
                            default:
                              return ['', ...mongoIdSource.map((value) => value.title)]
                          }
                        },
                      },
                    },
                    minLength: 1,
                  },
                  orderColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'order_columns',
                    items: {
                      type: 'object',
                      properties: {
                        name: { title: 'column_name', type: 'string' },
                      },
                    },
                  },
                  pageSize: { title: 'page_size', type: 'number' },
                  infiniteScroll: {
                    title: 'use_infinite_scroll',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                  defaultEmpty: {
                    title: 'default_empty_list',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
              layoutIds: {
                title: 'layouts',
                type: 'object',
                options: {
                  collapsed: false,
                },
                properties: {
                  wrapperLayout: {
                    title: 'wrapper_layout_name',
                    type: 'number',
                    enum: [0, ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['-', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 0,
                  },
                  filterLayout: {
                    title: 'filter_layout_name',
                    type: 'number',
                    enum: [0, ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['-', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 0,
                  },
                  listItemLayout: {
                    title: 'list_item_layout_name',
                    type: 'number',
                    enum: [0, ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['-', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 0,
                  },
                  emptyLayout: {
                    title: 'empty_layout_name',
                    type: 'number',
                    enum: [0, ...templateIdSource.map((value) => value.value)],
                    options: {
                      enum_titles: ['-', ...templateIdSource.map((value) => value.title)],
                      select2_options: {
                        placeholder: '',
                        allowClear: true,
                      },
                    },
                    minLength: 0,
                  },
                },
              },
              exportSettings: {
                title: 'export_settings',
                type: 'object',
                options: {
                  collapsed: true,
                },
                properties: {
                  showExportButton: {
                    title: 'show_list_data_export_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 12 },
                    default: true,
                  },
                  exportColumns: {
                    type: 'array',
                    format: 'table',
                    title: 'export_columns',
                    items: {
                      type: 'object',
                      title: 'add_column',
                      properties: {
                        name: { title: 'column_name', type: 'string' },
                      },
                    },
                  },
                  exportTypes: {
                    title: 'exportable_filetypes',
                    type: 'object',
                    format: 'grid',
                    options: { disable_collapse: true },
                    properties: {
                      html: {
                        title: 'HTML',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 3 },
                        default: true,
                      },
                      csv: {
                        title: 'CSV',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 4 },
                        default: true,
                      },
                      txt: {
                        title: 'TXT',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 5 },
                        default: true,
                      },
                      pdf: {
                        title: 'PDF',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 3 },
                        default: true,
                      },
                      xlsx: {
                        title: 'Excel 2007 +',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 4 },
                        default: true,
                      },
                      ods: {
                        title: 'OpenDocument Spreadsheet',
                        type: 'boolean',
                        format: 'checkbox',
                        options: { grid_columns: 5 },
                        default: true,
                      },
                    },
                  },
                  exportLimit: {
                    title: 'limit_of_exportable_rows_(0_for_no_limit)',
                    type: 'number',
                    default: 100,
                  },
                  exportLimitInformation: {
                    title: 'limit_information_text',
                    type: 'string',
                    default: 'export_limit_notification',
                  },
                  exportAll: {
                    title: 'export_all',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  exportPage: {
                    title: 'export_page',
                    type: 'boolean',
                    format: 'checkbox',
                    default: true,
                  },
                  exportOriginalType: {
                    title: 'export_list_in_original_type',
                    type: 'boolean',
                    format: 'checkbox',
                    default: false,
                  },
                },
              },
              hamburgerSettings: {
                title: 'hamburger_settings',
                options: {
                  collapsed: true,
                },
                type: 'object',
                format: 'grid',
                properties: {
                  showGroupActionsButton: {
                    title: 'show_group_actions_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  runGroupActionsOnAllWithoutSelect: {
                    title: 'run_group_actions_on_all_without_select',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showRowSelectionFilter: {
                    title: 'show_row_selection_filter',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showQueueActionsButton: {
                    title: 'show_queue_actions_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                  showSavedFilter: {
                    title: 'show_saved_filter',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showResetButton: {
                    title: 'show_reset_button',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showListOrder: {
                    title: 'show_list_order',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  showPageLimiter: {
                    title: 'show_page_limiter',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: true,
                  },
                  filterNullEnabled: {
                    title: 'filter_null_enabled',
                    type: 'boolean',
                    format: 'checkbox',
                    options: { grid_columns: 6 },
                    default: false,
                  },
                },
              },
              buttonOverrides: {
                type: 'array',
                format: 'grid-strict',
                title: 'buttons_overwrite',
                uniqueItems: true,
                items: {
                  type: 'object',
                  format: 'grid-strict',
                  title: 'button_overwrite',
                  headerTemplate: '{{ self.name }} - {{ self.selector }}',
                  properties: {
                    selector: {
                      title: 'selector',
                      type: 'string',
                      options: { grid_columns: 6 },
                    },
                    name: {
                      title: 'label',
                      type: 'string',
                      options: { grid_columns: 4 },
                    },
                    seq: {
                      title: 'seq',
                      type: 'string',
                      options: { grid_columns: 2 },
                    },
                    url: {
                      title: 'URL',
                      type: 'string',
                      options: { grid_columns: 8 },
                    },
                    target: {
                      title: 'target',
                      type: 'string',
                      enum: ['_self', '_blank'],
                      options: { grid_columns: 4 },
                    },
                    buttonType: {
                      title: 'button_type',
                      type: 'string',
                      enum: ['primary', 'secondary', 'info', 'warning', 'danger', 'success'],
                      options: { grid_columns: 4 },
                    },
                    buttonStyle: {
                      title: 'style',
                      type: 'string',
                      options: { grid_columns: 4 },
                    },
                    icon: {
                      title: 'icon',
                      type: 'string',
                      options: { grid_columns: 4 },
                    },
                    hidden: {
                      title: 'hidden',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 4 },
                    },
                    groupHidden: {
                      title: 'group_hidden',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 4 },
                    },
                    popup: {
                      title: 'popup',
                      type: 'boolean',
                      format: 'checkbox',
                      default: false,
                      options: { grid_columns: 4 },
                    },
                  },
                },
              },
              hiddenSearchData: {
                type: 'object',
                title: 'extra_filter',
                properties: {
                  where: {
                    type: 'array',
                    format: 'table',
                    title: 'conditions',
                    items: {
                      type: 'object',
                      title: 'condition',
                      properties: {
                        key: { title: 'column_name', type: 'string' },
                        op: {
                          title: 'operation',
                          type: 'string',
                          enum: OPERATIONS.map((op) => op.value),
                          options: {
                            enum_titles: OPERATIONS.map((op) => op.key),
                            select2_options: {
                              default: '=',
                              allowClear: false,
                            },
                          },
                        },
                        value: { title: 'value', type: 'string' },
                      },
                    },
                  },
                  logic: {
                    type: 'string',
                    title: 'logic',
                  },
                  filterExp: {
                    type: 'object',
                    title: 'filter_expression',
                    properties: {
                      table: { type: 'string', title: 'table_name' },
                      // select: {type: 'array', title: 'Field name pl: ['ID_ALLASKERESO']'},
                      select: {
                        type: 'array',
                        format: 'table',
                        title: 'field_name_ex:_ID_ALLASKERESO',
                        items: {
                          type: 'string',
                          title: 'field_name',
                          properties: {
                            name: { title: 'Name', type: 'string' },
                          },
                        },
                      },
                      field: { type: 'string', title: 'field_name' },
                      operation: {
                        title: 'operation',
                        type: 'string',
                        enum: ['', 'in', 'not in'],
                      },
                      where: {
                        type: 'array',
                        format: 'table',
                        title: 'conditions',
                        uniqueItems: true,
                        items: {
                          type: 'object',
                          title: 'condition',
                          properties: {
                            key: { title: 'column_name', type: 'string' },
                            op: {
                              title: 'operation',
                              type: 'string',
                              enum: OPERATIONS.map((op) => op.value),
                              options: {
                                enum_titles: OPERATIONS.map((op) => op.key),
                                select2_options: {
                                  default: '=',
                                  allowClear: false,
                                },
                              },
                            },
                            value: { title: 'value', type: 'string' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              controllerName: {
                title: 'controller_name',
                type: 'string',
              },
            },
          },
        },
      }
      options.schema = deepmerge(options.schema, schema)
      break
    }
    case PANEL_TYPES.DYN_FORM_NEW: {
      const specifiedOptions = {
        subformView: {
          title: 'subform_view',
          type: 'string',
          enum: ['Default', 'Grid', 'List', 'FormGrid', 'CollapsibleGrid'],
          default: 'Default',
        },
        showNotiOnExp: {
          title: 'show_notification_when_expression_calculates:',
          type: 'object',
          collapsed: false,
          properties: {
            value: {
              title: 'value',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
            hidden: {
              title: 'hidden_property',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
          },
        },
        showIsDirtyWarningMessage: {
          title: 'show_is_dirty_warning_message',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        usernameHidden: {
          title: 'username_hidden',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        groupAutoCollapse: {
          title: 'group_auto_collapse',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        collapsedDefaultClose: {
          title: 'group_collapsed_default_close',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        formDataVersionButtons: {
          title: 'form_data_version_button_settings',
          type: 'object',
          collapsed: false,
          properties: {
            showCloneButton: {
              title: 'show_clone_button',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            showCopyButton: {
              title: 'show_copy_button',
              type: 'boolean',
              format: 'checkbox',
              default: false,
            },
            showVersionButton: {
              title: 'show_version_button',
              type: 'boolean',
              format: 'checkbox',
              default: true,
            },
          },
        },
        showHistory: {
          title: 'enable_form_data_history',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        showFormVersionInfo: {
          title: 'enable_form_version_information',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        blockchainEnabled: {
          title: 'enable_blockchain',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        mergeUrlParams: {
          title: 'merge_data_with_url_params_(this_checkbox_is_only_for_the_updatemethod)',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        saveOnEnter: {
          title: 'save_form_on_enter',
          type: 'boolean',
          format: 'checkbox',
          default: false,
        },
        buttons: {
          type: 'object',
          title: 'buttons_override',
          options: {
            collapsed: false,
          },
          properties: {
            save: {
              type: 'object',
              format: 'grid',
              title: 'save_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'save',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_save',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            saveAndNew: {
              type: 'object',
              format: 'grid',
              title: 'save_and_new_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'saveAndNew',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_saveAndNew',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            saveAndClose: {
              type: 'object',
              format: 'grid',
              title: 'save_and_close_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'saveAndClose',
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_saveAndClose',
                  options: { grid_columns: 3 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 3 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 2 },
                },
                showDefaultNotification: {
                  title: 'show_default_noti',
                  type: 'boolean',
                  default: true,
                  options: { grid_columns: 3 },
                },
              },
            },
            close: {
              type: 'object',
              format: 'grid',
              title: 'close_button',
              properties: {
                event: {
                  title: 'event_name',
                  type: 'hidden',
                  default: 'index',
                  disable: true,
                  options: { grid_columns: 1 },
                },
                title: {
                  title: 'title',
                  type: 'string',
                  default: 'dynform_button_close',
                  options: { grid_columns: 4 },
                },
                redirectUrl: {
                  title: 'redirect_url',
                  type: 'string',
                  options: { grid_columns: 4 },
                },
                hidden: {
                  title: 'hidden',
                  type: 'boolean',
                  default: false,
                  options: { grid_columns: 3 },
                },
              },
            },
          },
        },
        idForm: {
          type: 'string',
          title: 'form',
          enum: ['', ...mongoIdSource.map((value) => value.value)],
          options: {
            enum_titles: ['', ...mongoIdSource.map((value) => value.title)],
            select2_options: {
              placeholder: '',
              allowClear: true,
              dropdownAutoWidth: true,
              // containerCss: {'display':'block', 'width':'100%'},
              width: '300px',
            },
          },
          minLength: 1,
          propertyOrder: 0,
        },
      }

      options.schema.properties.base.properties = deepmerge(options.schema.properties.base.properties, specifiedOptions)
      break
    }
    default: {
      const schema = {
        properties: {},
      }
      options.schema = deepmerge(options.schema, schema)
    }
  }

  return options
}
